// Browser globals (window, document, navigator, localStorage, location, history)
// are provided by ESLint's globals.browser preset — no `/* global */` comment
// needed under the flat config (see eslint.config.js).
/*
 * HealBot i18n runtime — UA (default) + RU, EN, PL, RO with auto-detection.
 *
 * Surface:
 *   window.i18n shape:
 *     SUPPORTED, DEFAULT,
 *     strings,             // { uk: {...}, ru: {...}, en: {...}, pl: {...}, ro: {...} }
 *     t(key, fallback),    // translate a dotted key
 *     getLocale(),         // "uk" | "ru" | "en" | "pl" | "ro"
 *     setLocale(locale),   // switch locale + persist + re-render + sync URL prefix
 *     numberLocale(locale) // "uk-UA" | "ru-RU" | "en-GB" | "pl-PL" | "ro-RO" for toLocaleString().
 *
 * Persistence precedence (read on load):
 *   URL prefix (/ru/, /en/, /pl/, /ro/) -> localStorage.healbot_locale ->
 *   cookie healbot_locale -> navigator.languages -> DEFAULT.
 *
 * On setLocale (or initial detect): localStorage + cookie written,
 * URL prefix synced via history.replaceState (no full reload),
 * <html lang>, <title>, <meta name="description"> updated,
 * window event "i18n:change" dispatched so App.jsx can trigger re-render.
 *
 * Per-locale fallback chain (used by t() when a key is missing in the active locale):
 *   ru -> uk, pl -> uk, ro -> uk, en -> uk, uk -> en (cross-fallback to widest).
 *
 * Debug logs: set window.__I18N_DEBUG__ = true before load.
 */

(function () {
  const SUPPORTED = ["uk", "ru", "en", "pl", "ro"];
  // DEFAULT can be overridden per-snapshot by a `<script>window.__HB_DEFAULT_LOCALE__=...</script>`
  // tag injected by scripts/build-locale-html.js (Phase 5.3). This lets each
  // path-prefixed static HTML default to its own locale even when SPA hash
  // navigation re-renders before our detect() can read the URL.
  const DEFAULT_OVERRIDE =
    typeof window !== "undefined" &&
    typeof window.__HB_DEFAULT_LOCALE__ === "string" &&
    SUPPORTED.indexOf(window.__HB_DEFAULT_LOCALE__) !== -1
      ? window.__HB_DEFAULT_LOCALE__
      : null;
  const DEFAULT = DEFAULT_OVERRIDE || "uk";
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

  // URL prefix detector for /ru/, /en/, /pl/, /ro/ (uk is implicit / no prefix).
  // Matches the leading segment only; subsequent path is captured in group 2.
  const LOCALE_PATH_RE = /^\/(ru|en|pl|ro)(\/|$)/;

  // Per-locale fallback chain. Keys missing in the active locale fall through
  // these in order before t() returns the literal key.
  //
  // EU locales (en/pl/ro) prefer English over Ukrainian per plan §1.3:
  // "a missing PL/RO key should prefer English over Ukrainian for EU users".
  // RU keeps the legacy ru -> uk chain because RU/UK share core market vocab.
  const FALLBACK = {
    ru: ["uk", "en"],
    pl: ["en", "uk"],
    ro: ["en", "uk"],
    en: ["uk"],
    uk: ["en"],
  };

  const dbg = (...args) => {
    // eslint-disable-next-line no-console -- gated debug log; opt-in via window.__I18N_DEBUG__
    if (window.__I18N_DEBUG__) console.debug("[i18n]", ...args);
  };

  const strings = {
    uk: {
      // ---------- meta ----------
      "meta.title": "HealBot — AI-асистент для приватних клінік",
      "meta.description":
        "HealBot — AI-асистент для приватних клінік: цілодобовий запис пацієнтів, автоматизація реєстратури та зниження навантаження на адміністраторів.",

      // ---------- nav ----------
      "nav.aria.main": "Main",
      "nav.aria.openmenu": "Відкрити меню",
      "nav.aria.closemenu": "Закрити меню",
      "nav.aria.telegram": "Написати в Telegram",
      "nav.aria.lang": "Мова інтерфейсу",
      "nav.link.problem": "Як працює",
      "nav.link.directions": "Напрямки",
      "nav.link.case": "Кейси",
      "nav.link.calculator": "Калькулятор",
      "nav.link.pricing": "Тарифи",
      "nav.link.faq": "FAQ",
      "nav.cta.telegram": "Telegram",
      "nav.cta.demo": "Забронювати демо",
      "nav.drawer.telegram": "Написати в Telegram",

      // ---------- phone mockup ----------
      "phone.aria.image": "Макет iPhone 15 з Telegram-інтерфейсом HealBot",
      "phone.status.line": "online · відповідає зараз",
      "phone.tg.date": "Сьогодні",
      "phone.tg.input.placeholder": "Повідомлення",
      "phone.hero.1": "Добрий день! Хочу записатись на прийом до мамолога",
      "phone.hero.2": "Вітаю 👋 Підкажіть, чи є у вас уже результати УЗД або мамографії?",
      "phone.hero.3": "Так, УЗД робила 3 тижні тому",
      "phone.hero.4": "Чудово. Пропоную три найближчі вікна:",
      "phone.hero.4.q1": "Завтра, 10:40",
      "phone.hero.4.q2": "Чт, 15:20",
      "phone.hero.4.q3": "Пт, 09:00",
      "phone.hero.5": "Завтра, 10:40 підійде",
      "phone.hero.6": "Записала вас до Надії Іванівни. Надішлю нагадування за 2 години 💛",
      "phone.case.1": "Добрий вечір, болить груди, що робити?",
      "phone.case.2": "Співчуваю. Уточніть, будь ласка: біль з одного боку чи з обох?",
      "phone.case.3": "Тільки справа",
      "phone.case.4": "Зрозуміла. Пропоную записатись на прийом найближчим часом:",
      "phone.case.4.q1": "Завтра, 09:30",
      "phone.case.4.q2": "Завтра, 14:00",

      // ---------- hero ----------
      "hero.chip": "Працюємо з 2024",
      "hero.h1.line1": "AI-асистент,",
      "hero.h1.line2a": "який бере",
      "hero.h1.line2b": "рутину реєстратури",
      "hero.h1.line3": "на себе — у Telegram.",
      "hero.lede":
        "Записує пацієнток, відповідає на часті питання, нагадує про прийом — 24/7, вашою мовою. Без заміни адміністратора, але без вигорання.",
      "hero.cta.primary": "Забронювати демо",
      "hero.cta.secondary": "Подивитись живий бот",
      "hero.metrics.aria": "Метрики ефективності",
      "hero.metric.1.tag": "placeholder",
      "hero.metric.1.tag.verified": "підтверджено",
      "hero.metric.1.label": "зростання онлайн-записів",
      "hero.metric.1.label.sub": "за 3 місяці",
      "hero.metric.2.tag": "placeholder",
      "hero.metric.2.tag.verified": "підтверджено",
      "hero.metric.2.unit": " хв",
      "hero.metric.2.label": "середній час",
      "hero.metric.2.label.sub": "відповіді пацієнтці",
      "hero.metric.3.tag": "placeholder",
      "hero.metric.3.tag.verified": "підтверджено",
      "hero.metric.3.unit": " год/тиж",
      "hero.metric.3.label": "звільняє",
      "hero.metric.3.label.sub": "реєстратуру",
      "hero.video.title": "Як працює за 60 сек",
      "hero.video.meta": "autoplay · muted · loop",

      // ---------- problem ----------
      "problem.eyebrow": "Проблема",
      "problem.h2.pre": "Адмін витрачає",
      "problem.h2.hl": "60% часу",
      "problem.h2.post": "на повтори одного й того ж.",
      "problem.lede":
        "Кожна друга пацієнтка йде ще до того, як ви встигли відповісти. Не тому що у вас поганий сервіс — просто телефон не гумовий, а Instagram не читає сам себе.",
      "problem.quote.1.text":
        "Я намагалась додзвонитись з 9 ранку — ніхто не відповідав. Зрештою записалась до іншої клініки.",
      "problem.quote.1.name": "Пацієнтка, 34",
      "problem.quote.1.sub": "цитата з розмови, листопад 2024",
      "problem.quote.2.text":
        "Написала в Instagram у суботу — відповіли лише у вівторок. На той момент уже з'їздила до приватної лабораторії.",
      "problem.quote.2.name": "Пацієнтка, 41",
      "problem.quote.2.sub": "цитата з опитування пацієнток приватної клініки",
      "problem.quote.3.text":
        "Хотіла уточнити, чи потрібно натщесерце — 15 хвилин на утриманні слухала музику.",
      "problem.quote.3.name": "Пацієнтка, 52",
      "problem.quote.3.sub": "цитата з відгуку Google",
      "problem.leak.h3": "А пацієнтки тим часом ідуть до конкурентів.",
      "problem.leak.p":
        "Середня приватна клініка втрачає 20–40 записів на місяць просто через пропущені повідомлення.",
      "problem.leak.stat": "→ −180 000 ₴ / міс",

      // ---------- directions ----------
      "directions.eyebrow": "Напрямки",
      "directions.h2": "Один бот, сценарії під кожен напрямок.",
      "directions.note":
        "Усі 11 напрямків уже працюють. Бот відповідає з бази знань вашої клініки та записує пацієнтів, але не ставить діагнозів і не тлумачить результати. Новий напрямок — за запитом.",
      "directions.badge.live": "live",
      "directions.badge.soon": "soon",
      "directions.side.title": "Приклад сценарію",
      "directions.default.scen.in": "Запишіть мене, будь ласка, на прийом",
      "directions.default.scen.in.label": "Пацієнтка",
      "directions.default.scen.out": "Звісно. Підкажіть напрямок та зручний час.",
      "directions.default.scen.out.label": "HealBot",
      // mammo
      "directions.card.mammo.label": "Мамологія",
      "directions.card.mammo.h": "Запис на мамологічний прийом без зайвих дзвінків",
      "directions.card.mammo.d":
        "Бот відповідає на питання про ціни, підготовку й лікарів з бази знань клініки та передає заявку адміністратору — без діагнозів і тлумачення результатів.",
      "directions.card.mammo.feat.1":
        "Вхідний квіз на основі моделі Gail / NCCN і наказу МОЗ №1368 — допомагає обрати вид прийому",
      "directions.card.mammo.feat.2": "Підготовка в нагадуванні: обстеження на 5–12 день циклу",
      "directions.card.mammo.feat.3":
        "Нагадування за 72, 24 та 2 год до візиту; повторний виклик через 2 роки",
      "directions.card.mammo.feat.4":
        "Червоні прапорці перехоплюються до AI: фіксована відповідь 103/112 і передача адміністратору",
      "directions.card.mammo.scen.in": "Коли краще прийти на УЗД молочних залоз?",
      "directions.card.mammo.scen.out":
        "Оптимально на 5–12 день циклу. Залиште ім'я та телефон — адміністратор передзвонить і підтвердить час, а я нагадаю про візит.",
      // gyn
      "directions.card.gyn.label": "Гінекологія",
      "directions.card.gyn.h": "Делікатний запис до гінеколога",
      "directions.card.gyn.d":
        "Відповідає на організаційні питання й записує на прийом; лікування не призначає, тлумачень не дає.",
      "directions.card.gyn.feat.1":
        "Квіз про скринінг шийки матки (ACS 2025 / настанова МОЗ 2024): з 25 років, ВПЛ-тест раз на 5 років",
      "directions.card.gyn.feat.2": "Підготовка до цитології — у нагадуванні перед візитом",
      "directions.card.gyn.feat.3":
        "Червоні прапорці вагітності одразу отримують екстрений шаблон і передаються адміністратору",
      "directions.card.gyn.feat.4": "Нагадування за 72, 24 та 2 год до візиту",
      "directions.card.gyn.scen.in": "Коли краще прийти на цитологію?",
      "directions.card.gyn.scen.out":
        "За правилами клініки — не під час менструації, за 48 год без вагінальних засобів. Нагадаю про підготовку за добу. Записати вас?",
      // dent
      "directions.card.dent.label": "Стоматологія",
      "directions.card.dent.h": "Запис і повторні огляди в стоматології",
      "directions.card.dent.d":
        "Ціни, тривалість процедур і запис — з бази знань клініки. Бот не ставить діагнозів: план лікування складає лікар на огляді.",
      "directions.card.dent.feat.1": "Квіз за формою ADA про фактори ризику карієсу",
      "directions.card.dent.feat.2":
        "Інтервал повторних оглядів за факторами (NICE CG19): 3, 6 або 12 місяців",
      "directions.card.dent.feat.3": "Нагадування про план лікування через 3 дні після візиту",
      "directions.card.dent.feat.4": "Дожим тих, хто залишив телефон, але не записався",
      "directions.card.dent.scen.in": "Скільки триває професійна чистка?",
      "directions.card.dent.scen.out":
        "Близько години. Залиште ім'я, телефон і зручний час — адміністратор передзвонить і підтвердить запис.",
      // eye
      "directions.card.eye.label": "Офтальмологія",
      "directions.card.eye.h": "Запис на перевірку зору та діагностику",
      "directions.card.eye.d":
        "Підкаже вартість і тривалість обстежень, запише на прийом і нагадає про підготовку; висновки робить тільки лікар.",
      "directions.card.eye.feat.1":
        "Квіз за AAO PPP 2026 і ADA (огляд при діабеті) — щоб обрати вид прийому",
      "directions.card.eye.feat.2":
        "Попередження: після розширення зіниці кілька годин не сідати за кермо",
      "directions.card.eye.feat.3":
        "Раптова втрата зору чи спалахи — екстрений шаблон і передача адміністратору",
      "directions.card.eye.feat.4": "Нагадування за 72, 24 та 2 год до візиту",
      "directions.card.eye.scen.in": "Чи можна після огляду одразу сідати за кермо?",
      "directions.card.eye.scen.out":
        "Якщо лікар розширюватиме зіницю — ні, кілька годин зір буде розмитим. Краще приїхати із супроводом або на таксі. Записати вас на огляд?",
      // ped
      "directions.card.ped.label": "Педіатрія",
      "directions.card.ped.h": "Щеплення та планові огляди дітей",
      "directions.card.ped.d":
        "Відповідає батькам про щеплення, огляди й ціни та записує дитину; небезпечні симптоми одразу йдуть в екстрений шаблон.",
      "directions.card.ped.feat.1":
        "Квіз за календарем щеплень МОЗ (редакція 2026), оглядами Bright Futures і віхами розвитку",
      "directions.card.ped.feat.2":
        "Червоні прапорці за NICE: до 3 місяців і 38 °C, висип, що не блідне, судоми → 103/112",
      "directions.card.ped.feat.3": "«Візьміть карту щеплень» — у нагадуванні перед візитом",
      "directions.card.ped.feat.4": "Повторний виклик на плановий огляд за інтервалом клініки",
      "directions.card.ped.scen.in": "Що взяти з собою на щеплення?",
      "directions.card.ped.scen.in.label": "Мама",
      "directions.card.ped.scen.out":
        "Карту щеплень і документи дитини. Перед щепленням дитину огляне педіатр. Нагадаю за добу. Записати на зручний час?",
      // lab
      "directions.card.lab.label": "Лабораторія",
      "directions.card.lab.h": "Підготовка до аналізів і запис на забір",
      "directions.card.lab.d":
        "Допомагає обрати чекап-пакет, пояснює підготовку та записує на забір; результати ніколи не тлумачить — їх обговорюють з лікарем.",
      "directions.card.lab.feat.1": "Підбір чекап-пакета за віком і факторами FINDRISC",
      "directions.card.lab.feat.2": "Підготовка натще — у нагадуванні перед забором",
      "directions.card.lab.feat.3": "Через 3 дні: «результати готові — обговоріть їх з лікарем»",
      "directions.card.lab.feat.4": "Є скарги — бот веде до лікаря, а не до самостійного пакета",
      "directions.card.lab.scen.in": "Завтра здаю біохімію. Можна випити каву зранку?",
      "directions.card.lab.scen.out":
        "Ні, біохімію здають натще: 8–12 годин без їжі, лише вода. Каву можна одразу після забору.",
      // cos
      "directions.card.cos.label": "Косметологія",
      "directions.card.cos.h": "Консультації та запис на процедури",
      "directions.card.cos.d":
        "Розповідає про процедури, ціни й підготовку з бази знань клініки та записує на консультацію; результатів не обіцяє.",
      "directions.card.cos.feat.1":
        "Фототип за Fitzpatrick і протипоказання: вагітність, ізотретиноїн",
      "directions.card.cos.feat.2": "Родимка, що змінюється, — спершу до дерматолога",
      "directions.card.cos.feat.3": "Питання «як ви почуваєтесь?» через 24 год після процедури",
      "directions.card.cos.feat.4":
        "Ускладнення після ін'єкцій — екстрений шаблон і передача адміністратору",
      "directions.card.cos.scen.in": "Коли краще робити біоревіталізацію перед відпусткою?",
      "directions.card.cos.scen.out":
        "За правилами клініки — щонайменше за 2 тижні до активного сонця. Спершу консультація, на якій лікар перевірить протипоказання. Записати вас?",
      // gen
      "directions.card.gen.label": "Загальна практика",
      "directions.card.gen.h": "Підкаже, до якого лікаря записатись",
      "directions.card.gen.d":
        "Спрямовує скаргу до профільного спеціаліста й збирає заявку; спершу відсіює невідкладні стани, діагнозів не ставить.",
      "directions.card.gen.feat.1": "Маршрутизація скарги до профільного спеціаліста",
      "directions.card.gen.feat.2":
        "Спершу перевіряє невідкладні стани: інсульт, біль у грудях, сепсис → 103/112",
      "directions.card.gen.feat.3": "Кваліфікація заявки: до якого лікаря і в який зручний час",
      "directions.card.gen.feat.4":
        "Нагадування за 72, 24 та 2 год і повторний виклик за інтервалом клініки",
      "directions.card.gen.scen.in": "Тижнями болить поперек. До кого мені записатись?",
      "directions.card.gen.scen.out":
        "З таким питанням у нас приймає невролог — вт, чт, сб. Залиште ім'я та телефон, і адміністратор підбере час.",
      // gastro
      "directions.card.gastro.label": "Гастроентерологія",
      "directions.card.gastro.h": "Гастроентерологія та ендоскопія",
      "directions.card.gastro.d":
        "Записує на консультацію чи ендоскопію й вчасно надсилає інструкцію з підготовки — текст вашої клініки.",
      "directions.card.gastro.feat.1":
        "Квіз-маршрутизатор: тривалість скарг, колоректальний скринінг з 45 років, сімейний анамнез",
      "directions.card.gastro.feat.2":
        "Підготовка до колоноскопії та гастроскопії — у нагадуваннях за 72, 24 і 12 год",
      "directions.card.gastro.feat.3":
        "Кров у калі, чорний кал чи блювання кров'ю — екстрений шаблон і передача адміністратору",
      "directions.card.gastro.feat.4": "Дожим тих, хто залишив телефон, але не записався",
      "directions.card.gastro.scen.in": "Мене записали на колоноскопію. Як підготуватися?",
      "directions.card.gastro.scen.in.label": "Пацієнт",
      "directions.card.gastro.scen.out":
        "Інструкцію клініки надішлю частинами: за 72 год — дієта, за 24 і 12 год — прийом препарату для очищення. Якщо щось незрозуміло — підкаже адміністратор.",
      // uro
      "directions.card.uro.label": "Урологія",
      "directions.card.uro.h": "Урологія та андрологія",
      "directions.card.uro.d":
        "Делікатний режим: нагадування з нейтральним текстом, без назви послуги в сповіщеннях.",
      "directions.card.uro.feat.1":
        "Нейтральні нагадування — назва послуги не видна в прев'ю сповіщення",
      "directions.card.uro.feat.2":
        "Квіз лише для маршрутизації: до якого лікаря і на яку процедуру",
      "directions.card.uro.feat.3": "Підготовка до УЗД та спермограми — у нагадуванні",
      "directions.card.uro.feat.4":
        "Нагадування про контроль ПСА — за інтервалом, який призначив лікар",
      "directions.card.uro.scen.in": "Як підготуватися до спермограми?",
      "directions.card.uro.scen.in.label": "Пацієнт",
      "directions.card.uro.scen.out":
        "За інструкцією клініки — 2–7 днів утримання, без алкоголю й сауни. Нагадування прийде з нейтральним текстом. Записати вас?",
      // derm
      "directions.card.derm.label": "Дерматовенерологія",
      "directions.card.derm.h": "Дерматологія та венерологія",
      "directions.card.derm.d":
        "Записує до дерматолога й нагадує про щорічний огляд родимок; фото не оцінює — це робить лікар на прийомі.",
      "directions.card.derm.feat.1":
        "Делікатний режим для венерології: нейтральні тексти нагадувань",
      "directions.card.derm.feat.2": "Щорічне нагадування про картування родимок",
      "directions.card.derm.feat.3": "Родимка, що змінюється, — пріоритетний запис до лікаря",
      "directions.card.derm.feat.4": "Фото не оцінює: оцінку дає тільки дерматолог на прийомі",
      "directions.card.derm.scen.in": "Родимка за місяць збільшилась. Ось фото — це небезпечно?",
      "directions.card.derm.scen.out":
        "Оцінити родимку може лише лікар на огляді — за фото я цього не роблю. Поставлю вас у пріоритетний запис до дерматолога. Залиште ім'я та телефон.",

      // ---------- case ----------
      "case.brand": "Кейси · приватні клініки",
      "case.quote":
        "«Реальні кейси клінік-партнерів — скоро. За запитом готові поділитись метриками впроваджень під NDA.»",
      "case.pdf.aria": "Приклад звіту з аналітикою",
      "case.pdf.title": "Звіт за період",
      "case.pdf.sub": "HealBot analytics",
      "case.pdf.line.1": "Записів через бот",
      "case.pdf.line.2": "Відповідей поза годинами",
      "case.pdf.line.3": "Середня відповідь",
      "case.pdf.line.4": "Задоволеність",
      "case.pdf.empty": "—",
      "case.cta.full": "Читати повний кейс →",

      // ---------- case study (full page) ----------
      "case.study.title": "Як мамологічна клініка повернула 40% пропущених записів",
      "case.study.subtitle": "Пілотне впровадження HealBot у приватній клініці в Києві",
      "case.study.clinic.codename": "приватна мамологічна клініка в Києві",
      "case.study.badge.verified": "Верифікований пілот",
      "case.study.intro.p1":
        "Адміністратор клініки приймав 80–120 повідомлень на день у Telegram, Instagram і телефоном. До 40% запитів губилися: відповіді приходили за 30+ хвилин, частина — лише наступного дня. Пацієнтки тим часом записувалися до конкурентів.",
      "case.study.intro.p2":
        "Команда клініки разом з HealBot за 7 робочих днів адаптувала сценарії під мамологічний прийом: триаж за скаргами, розпізнавання згадок BI-RADS, інтеграція з МІС для прямого запису у вільний слот. Адміністратор залишився на лінії — але вже як ескалаційний канал, а не первинний приймач.",
      "case.study.intro.p3":
        "За 3 місяці пілоту з'явилася стійка картина: бот тримає середній час відповіді нижче 2 хвилин 24/7, а адміністратор отримав 60% свого часу назад. Усі цифри нижче — реальні; джерело — експорт з МІС клініки + WayForPay-логи (для онлайн-оплат) за виміряний період.",
      "case.study.metrics.heading": "Що показав пілот",
      "case.study.metric.bookings.label": "зростання онлайн-записів",
      "case.study.metric.bookings.detail": "за 3 місяці порівняно з попереднім кварталом",
      "case.study.metric.reply.label": "середній час відповіді пацієнтці",
      "case.study.metric.reply.detail": "включно з нічним і вихідним трафіком",
      "case.study.metric.frontdesk.label": "годин на тиждень повернуто адміну",
      "case.study.metric.frontdesk.detail": "що раніше йшли на повтори типових питань",
      "case.study.metric.empty": "TBD",
      "case.study.timeline.heading": "Як це відбувалося",
      "case.study.quote.heading": "З першоджерела",
      "case.study.disclaimer.medical":
        "HealBot не є медичним пристроєм і не надає медичних порад. Усі рішення щодо діагностики та лікування приймає лікар клініки.",
      "case.study.disclaimer.results":
        "Наведені результати спостерігалися у одній приватній клініці протягом пілоту AI-асистента HealBot. Вони не є гарантією результатів для будь-якої іншої клініки.",
      "case.study.cta.primary": "Забронювати демо для своєї клініки",
      "case.study.back.home": "← Повернутись на головну",
      "case.study.published.label": "Опубліковано:",
      "case.study.measured.label": "Період вимірювання:",
      "case.study.measured.tbd": "буде вказано після публікації",
      "meta.case.title": "Кейс: HealBot у мамологічній клініці у Києві — реальні метрики",
      "meta.case.description":
        "Як приватна мамологічна клініка повернула 40% пропущених записів за 3 місяці пілоту HealBot. Реальні метрики, анонімізовані деталі, медичний disclaimer.",

      // ---------- calculator ----------
      "calc.eyebrow": "ROI-калькулятор",
      "calc.h2": "Скільки HealBot поверне в касу.",
      "calc.lede":
        "Рухайте повзунки під свою клініку — цифри перераховуються одразу. Розрахунок консервативний: беремо 60% повернення пропущених записів.",
      "calc.bullet.1": "1 година адміна = 200 ₴",
      "calc.bullet.2": "8% часу на повторних питаннях",
      "calc.bullet.3": "60% повернення пропущених заявок",
      "calc.slider.visits.label": "Пацієнтів на місяць",
      "calc.slider.visits.unit": "осіб",
      "calc.slider.visits.aria": "Пацієнтів на місяць",
      "calc.slider.avgCheck.label": "Середній чек прийому",
      "calc.slider.avgCheck.unit": "₴",
      "calc.slider.avgCheck.aria": "Середній чек",
      "calc.slider.missed.label": "Пропущених заявок",
      "calc.slider.missed.unit": "%",
      "calc.slider.missed.aria": "Пропущених заявок",
      "calc.result.saved.label": "Економія часу адміна",
      "calc.result.saved.sub": "на місяць",
      "calc.result.recovered.label": "Повернений дохід",
      "calc.result.recovered.sub": "на місяць",
      "calc.payback.title": "Окупність ≈ 7–9 днів",
      "calc.payback.sub": "на тарифі Pro · рахуємо від мінімальної вигоди",
      "calc.payback.unit": "дн.",

      // ---------- pricing ----------
      "pricing.eyebrow": "Тарифи",
      "pricing.h2": "Прозоро. Без прихованих лімітів.",
      "pricing.aria.currency": "Валюта",
      "pricing.per_month": "/ міс",
      // Starter
      "pricing.tier.starter.title": "Starter",
      "pricing.tier.starter.desc": "Для одиночних кабінетів і невеликих клінік на старті.",
      "pricing.tier.starter.feat.1": "1 напрямок на вибір",
      "pricing.tier.starter.feat.2": "До 600 діалогів / міс",
      "pricing.tier.starter.feat.3": "1 мова (апгрейд у Pro — мультимовність)",
      "pricing.tier.starter.feat.4": "Базові сценарії запису",
      "pricing.tier.starter.feat.5": "Лише Telegram",
      "pricing.tier.starter.feat.6": "Email-саппорт",
      "pricing.tier.starter.muted.1": "CRM-інтеграція",
      "pricing.tier.starter.muted.2": "Аналітика та звіти",
      "pricing.tier.starter.cta": "Почати",
      // Pro
      "pricing.tier.pro.title": "Pro",
      "pricing.tier.pro.desc": "Для клінік 2–5 лікарів, які хочуть масштабуватись без найму.",
      "pricing.tier.pro.feat.1": "До 3 напрямків",
      "pricing.tier.pro.feat.2": "До 3 000 діалогів / міс",
      "pricing.tier.pro.feat.3": "Telegram + WhatsApp Cloud API",
      "pricing.tier.pro.feat.4": "CRM: Bitrix24, AmoCRM, MEDESK, Doctor Eleks",
      "pricing.tier.pro.feat.5": "Повна аналітика та PDF-звіти",
      "pricing.tier.pro.feat.6": "Мультимовність (UA/RU/EN/PL/RO)",
      "pricing.tier.pro.feat.7": "Пріоритет-саппорт 24/7",
      "pricing.tier.pro.cta": "Забронювати демо",
      // Scale
      "pricing.tier.scale.title": "Scale",
      "pricing.tier.scale.desc": "Для мереж 5+ клінік і напрямків з великим потоком.",
      "pricing.tier.scale.feat.1": "Безліміт напрямків",
      "pricing.tier.scale.feat.2": "До 30 000 діалогів / міс, далі pay-per-use",
      "pricing.tier.scale.feat.3": "Кастомні інтеграції",
      "pricing.tier.scale.feat.4": "Dedicated акаунт-менеджер",
      "pricing.tier.scale.feat.5": "SLA 99.9% + інфра у ЄС",
      "pricing.tier.scale.feat.6": "Власна інфраструктура за запитом",
      "pricing.tier.scale.feat.7": "White-label опціонально",
      "pricing.tier.scale.cta": "Звʼязатися",
      // PPU
      "pricing.ppu.title": "Pay-per-use, якщо трафік непередбачуваний",
      "pricing.ppu.subtitle.a": "Платите лише за активні діалоги — ",
      "pricing.ppu.subtitle.b": " за розмову. Без абонентської плати.",
      "pricing.link.more": "Дізнатися більше",
      // VAT note (Phase 3.4 — рендериться лише на en/pl/ro; в UA-копії лишається для парності словників)
      "pricing.vat.note":
        "Ціни без ПДВ. Рахунки виставляє ФОП Грибан Руслан Григорович (Україна); клінікам ЄС за потреби формуємо партнерський інвойс через європейську юрособу.",
      // ---------- pricing — checkout modal (HB-PAY) ----------
      "pricing.checkout.modal.title": "Оформлення підписки",
      "pricing.checkout.modal.close": "Закрити",
      "pricing.checkout.modal.aria_label": "Оформлення оплати",
      "pricing.checkout.email.label": "Ваш email",
      "pricing.checkout.email.placeholder": "you@clinic.com",
      "pricing.checkout.email.invalid": "Введіть коректну email-адресу",
      "pricing.checkout.tier.prefix": "Тариф ",
      "pricing.checkout.submit.label": "Перейти до оплати",
      "pricing.checkout.submit.loading": "Зачекайте…",
      "pricing.checkout.magic.sent_title": "Перевірте пошту",
      "pricing.checkout.magic.sent_body":
        "Ми надіслали лист на {{email}} з посиланням для підтвердження. Відкрийте його, щоб продовжити до захищеної оплати WayForPay.",
      "pricing.checkout.magic.resend": "Надіслати ще раз",
      "pricing.checkout.magic.resend_disabled_hint": "Доступно за {{seconds}} сек",
      "pricing.checkout.error.turnstile": "Перевірка безпеки не пройшла. Спробуйте ще раз.",
      "pricing.checkout.error.busy": "Запит уже обробляється. Зачекайте кілька секунд.",
      "pricing.checkout.error.ratelimited": "Забагато спроб. Спробуйте пізніше.",
      "pricing.checkout.error.network": "Проблема зі з'єднанням. Перевірте інтернет і повторіть.",
      "pricing.checkout.error.generic": "Щось пішло не так. Напишіть нам у Telegram.",
      // Сторінки повернення (Phase 4.7)
      "pricing.checkout.success.title": "Оплата пройшла успішно",
      "pricing.checkout.success.body":
        "Дякуємо! Ми отримали оплату за тариф {{tier}}. Чек надіслано на вашу пошту — протягом 5 хвилин з вами зв'яжеться команда HealBot.",
      "pricing.checkout.failure.title": "Платіж не вдалося завершити",
      "pricing.checkout.failure.body":
        "Щось пішло не так. Якщо з вас усе ж зняли кошти — напишіть нам у Telegram, ми розберемось.",

      // ---------- comparison ----------
      "cmp.eyebrow": "Порівняння",
      "cmp.h2": "Колл-центр, реєстратура чи HealBot?",
      "cmp.p":
        "На дистанції від 3 місяців — бот економить у 3–5 разів більше, ніж будь-який з традиційних підходів.",
      "cmp.th.criterion": "Критерій",
      "cmp.th.cc": "Колл-центр",
      "cmp.th.rec": "Реєстратура на повний день",
      "cmp.th.hb": "HealBot",
      "cmp.row.1.l": "Вартість на місяць",
      "cmp.row.1.cc": "від 25 000 ₴",
      "cmp.row.1.rec": "від 18 000 ₴",
      "cmp.row.1.hb": "від 7 900 ₴",
      "cmp.row.2.l": "Середній час відповіді",
      "cmp.row.2.cc": "4–12 хвилин",
      "cmp.row.2.rec": "5–30 хвилин",
      "cmp.row.2.hb": "< 2 хвилин",
      "cmp.row.3.l": "Робота вночі та у вихідні",
      "cmp.row.3.cc": "Лише черговий",
      "cmp.row.3.rec": "Ні",
      "cmp.row.3.hb": "24 / 7",
      "cmp.row.4.l": "Аналітика та звіти",
      "cmp.row.4.cc": "Відсутня",
      "cmp.row.4.rec": "Ручні зведення",
      "cmp.row.4.hb": "PDF + дашборд",
      "cmp.row.5.l": "Мультимовність",
      "cmp.row.5.cc": "1–2 мови",
      "cmp.row.5.rec": "1 мова",
      "cmp.row.5.hb": "UA · RU · EN · PL · RO",

      // ---------- faq ----------
      "faq.eyebrow": "FAQ",
      "faq.h2": "Часті питання від головлікарів.",
      "faq.sub": "Якщо питання складніше — напишіть у Telegram, відповім особисто протягом дня.",
      "faq.cta.ask": "Поставити питання",
      "faq.item.1.q": "Як захищені персональні дані пацієнток?",
      "faq.item.1.a":
        "Усі діалоги шифруються у каналі та при зберіганні. За замовчуванням дані розміщуються в інфраструктурі команди HealBot; розміщення в ЄС-регіоні (Франкфурт/Амстердам) доступне на тарифі Pro і вище, інші регіони обговорюємо під час onboarding. GDPR-ready: підписуємо DPA, зберігаємо дані мінімально необхідний термін. Персональні медичні дані ніколи не надсилаються третім сторонам, зокрема Google (Gemini API) — у модель ідуть лише знеособлені сценарії.",
      "faq.item.2.q": "А якщо пацієнтка не користується Telegram?",
      "faq.item.2.a":
        "У HealBot є паралельний канал через Viber і веб-віджет на сайті клініки. Пацієнтка сама обирає, де їй зручніше. У середньому 78% ваших пацієнток уже користуються Telegram — ми перевіримо це на етапі демо.",
      "faq.item.3.q": "Працюєте з клініками у Польщі, Румунії, Казахстані?",
      "faq.item.3.a":
        "Так. Сценарії доступні мовами UA / RU / EN / PL / RO, для Казахстану підключаємо KZ-російську та казахську на замовлення. Підключаємо локальні МІС та месенджери, прийняті в кожній країні. Договір та рахунки — через ФОП в Україні (можлива оплата у $/€/₴/₸); для клінік ЄС за потреби оформлюємо партнерський invoice через європейську юрособу.",
      "faq.item.4.q": "Скільки займає впровадження?",
      "faq.item.4.a":
        "На типовий напрямок (мамологія, гінекологія, стоматологія) — 5–7 робочих днів від підписання до запуску. Адаптуємо під ваш регламент, підключаємо МІС, тестуємо на ваших кейсах. Інтеграція з нестандартною CRM — до 3 тижнів.",
      "faq.item.5.q": "Чи можна замінити адміна ботом?",
      "faq.item.5.a":
        "Ні, це не мета HealBot. Адмін залишається — але перестає захлинатися у повторних питаннях та отримує більше часу на пацієнток, які вже у клініці. За нашим досвідом впровадження, адмін витрачає на 60% менше часу на вхідні.",
      "faq.item.6.q": "Що буде, якщо бот не зможе відповісти?",
      "faq.item.6.a":
        "Бот ескалює діалог адміну з повним контекстом. Жодних «Вибачте, я вас не зрозумів» — завжди є fallback на живу людину, і пацієнтка це бачить. Рівень ескалації на типових напрямках — 8–12%.",

      // ---------- cta block ----------
      "cta.eyebrow": "Демо",
      "cta.h2": "Покажемо живий бот за 20 хвилин.",
      "cta.p":
        "Не презентація слайдів — дамо вам поспілкуватися з живим ботом у реальному часі та розберемо, як він вписується у ваш процес.",
      "cta.form.h3": "Забронювати демо",
      "cta.form.name.label": "Імʼя",
      "cta.form.name.placeholder": "Надія",
      "cta.form.clinic.label": "Клініка",
      "cta.form.clinic.placeholder": "Назва клініки",
      "cta.form.email.label": "Email",
      "cta.form.email.placeholder": "info@your-clinic.com",
      "cta.form.phone.label": "Телефон або Telegram",
      "cta.form.phone.placeholder": "+380 67 ...",
      "cta.form.submit": "Забронювати демо",
      "cta.form.sent": "Дякуємо, звʼяжемося сьогодні ✓",
      "cta.form.magiclink":
        "Ми надіслали посилання для входу в кабінет на вашу пошту. Перевірте вхідні.",
      "cta.form.error": "Не вдалося надіслати. Спробуйте ще раз або напишіть нам.",
      "cta.form.consent.pre": "Натискаючи кнопку, погоджуєтесь з ",
      "cta.form.consent.link": "політикою конфіденційності",
      "cta.form.consent.post": ". Дані не передаються третім сторонам.",
      "cta.tg.h3": "Або запитайте в Telegram — асистент відповість одразу.",
      "cta.tg.p":
        "З вами говоритиме той самий AI-асистент, якого ми ставимо клінікам: тарифи, можливості, GDPR — з нашої ж бази знань. Це і є демонстрація продукту. Коли потрібна людина, він передає розмову засновнику.",
      "cta.tg.name": "HealBot AI",
      "cta.tg.role": "асистент · база знань HealBot",
      "cta.tg.btn": "Написати асистенту",
      "cta.tg.hours": "Відповідає одразу · передає засновнику, коли потрібна людина",

      // ---------- footer ----------
      "footer.brand.body":
        "AI-асистент для приватних клінік. Працює у Telegram, Viber та веб-віджеті. Заснований у 2024, команда з Києва.",
      "footer.badge.gdpr": "GDPR-ready",
      "footer.badge.madein": "🇺🇦 Made in Ukraine",
      "footer.col.product.h": "Продукт",
      "footer.col.product.link.1": "Як працює",
      "footer.col.product.link.2": "Напрямки",
      "footer.col.product.link.3": "Кейси",
      "footer.col.product.link.4": "Калькулятор",
      "footer.col.product.link.5": "Тарифи",
      "footer.col.directions.h": "Напрямки",
      "footer.col.directions.link.1": "Мамологія",
      "footer.col.directions.link.2": "Гінекологія",
      "footer.col.directions.link.3": "Стоматологія",
      "footer.col.directions.link.4": "Офтальмологія",
      "footer.col.directions.link.5": "Педіатрія",
      "footer.col.company.h": "Компанія",
      "footer.col.company.link.1": "Про нас",
      "footer.col.company.link.2": "Блог",
      "footer.col.company.link.3": "Карʼєра",
      "footer.col.company.link.4": "Контакти",
      "footer.col.company.link.5": "Партнерам",
      "footer.col.resources.h": "Ресурси",
      "footer.col.resources.link.1": "Privacy",
      "footer.col.resources.link.2": "Terms",
      "footer.col.resources.link.3": "Cookies",
      "footer.col.resources.link.4": "GDPR",
      "footer.col.resources.link.5": "Status",
      "footer.col.resources.link.6": "Contact",
      "footer.copyright": "© 2024–2026 HealBot · ФОП Грибан Руслан Григорович · Україна",
    },

    ru: {
      // ---------- meta ----------
      "meta.title": "HealBot — AI-ассистент для частных клиник",
      "meta.description":
        "HealBot — AI-ассистент для частных клиник: круглосуточная запись пациентов, автоматизация регистратуры и снижение нагрузки на администраторов.",

      // ---------- nav ----------
      "nav.aria.main": "Main",
      "nav.aria.openmenu": "Открыть меню",
      "nav.aria.closemenu": "Закрыть меню",
      "nav.aria.telegram": "Написать в Telegram",
      "nav.aria.lang": "Язык интерфейса",
      "nav.link.problem": "Как работает",
      "nav.link.directions": "Направления",
      "nav.link.case": "Кейсы",
      "nav.link.calculator": "Калькулятор",
      "nav.link.pricing": "Тарифы",
      "nav.link.faq": "FAQ",
      "nav.cta.telegram": "Telegram",
      "nav.cta.demo": "Забронировать демо",
      "nav.drawer.telegram": "Написать в Telegram",

      // ---------- phone mockup ----------
      "phone.aria.image": "Макет iPhone 15 с Telegram-интерфейсом HealBot",
      "phone.status.line": "online · отвечает сейчас",
      "phone.tg.date": "Сегодня",
      "phone.tg.input.placeholder": "Сообщение",
      "phone.hero.1": "Добрый день! Хочу записаться на приём к маммологу",
      "phone.hero.2":
        "Здравствуйте 👋 Подскажите, есть ли у вас уже результаты УЗИ или маммографии?",
      "phone.hero.3": "Да, УЗИ делала 3 недели назад",
      "phone.hero.4": "Отлично. Предлагаю три ближайших окна:",
      "phone.hero.4.q1": "Завтра, 10:40",
      "phone.hero.4.q2": "Чт, 15:20",
      "phone.hero.4.q3": "Пт, 09:00",
      "phone.hero.5": "Завтра, 10:40 подойдёт",
      "phone.hero.6": "Записала вас к Надежде Ивановне. Пришлю напоминание за 2 часа 💛",
      "phone.case.1": "Добрый вечер, болит грудь, что делать?",
      "phone.case.2": "Сочувствую. Уточните, пожалуйста: боль с одной стороны или с обеих?",
      "phone.case.3": "Только справа",
      "phone.case.4": "Поняла. Предлагаю записаться на приём в ближайшее время:",
      "phone.case.4.q1": "Завтра, 09:30",
      "phone.case.4.q2": "Завтра, 14:00",

      // ---------- hero ----------
      "hero.chip": "Работаем с 2024",
      "hero.h1.line1": "AI-ассистент,",
      "hero.h1.line2a": "который берёт",
      "hero.h1.line2b": "рутину регистратуры",
      "hero.h1.line3": "на себя — в Telegram.",
      "hero.lede":
        "Записывает пациенток, отвечает на частые вопросы, напоминает о приёме — 24/7, на вашем языке. Без замены администратора, но без выгорания.",
      "hero.cta.primary": "Забронировать демо",
      "hero.cta.secondary": "Посмотреть живого бота",
      "hero.metrics.aria": "Метрики эффективности",
      "hero.metric.1.tag": "placeholder",
      "hero.metric.1.tag.verified": "подтверждено",
      "hero.metric.1.label": "рост онлайн-записей",
      "hero.metric.1.label.sub": "за 3 месяца",
      "hero.metric.2.tag": "placeholder",
      "hero.metric.2.tag.verified": "подтверждено",
      "hero.metric.2.unit": " мин",
      "hero.metric.2.label": "среднее время",
      "hero.metric.2.label.sub": "ответа пациентке",
      "hero.metric.3.tag": "placeholder",
      "hero.metric.3.tag.verified": "подтверждено",
      "hero.metric.3.unit": " ч/нед",
      "hero.metric.3.label": "освобождает",
      "hero.metric.3.label.sub": "регистратуру",
      "hero.video.title": "Как работает за 60 сек",
      "hero.video.meta": "autoplay · muted · loop",

      // ---------- problem ----------
      "problem.eyebrow": "Проблема",
      "problem.h2.pre": "Администратор тратит",
      "problem.h2.hl": "60% времени",
      "problem.h2.post": "на повторы одного и того же.",
      "problem.lede":
        "Каждая вторая пациентка уходит ещё до того, как вы успели ответить. Не потому что у вас плохой сервис — просто телефон не резиновый, а Instagram не читает сам себя.",
      "problem.quote.1.text":
        "Я пыталась дозвониться с 9 утра — никто не отвечал. В итоге записалась в другую клинику.",
      "problem.quote.1.name": "Пациентка, 34",
      "problem.quote.1.sub": "цитата из разговора, ноябрь 2024",
      "problem.quote.2.text":
        "Написала в Instagram в субботу — ответили только во вторник. К тому моменту уже съездила в частную лабораторию.",
      "problem.quote.2.name": "Пациентка, 41",
      "problem.quote.2.sub": "цитата из опроса пациенток частной клиники",
      "problem.quote.3.text":
        "Хотела уточнить, нужно ли натощак — 15 минут на удержании слушала музыку.",
      "problem.quote.3.name": "Пациентка, 52",
      "problem.quote.3.sub": "цитата из отзыва Google",
      "problem.leak.h3": "А пациентки тем временем уходят к конкурентам.",
      "problem.leak.p":
        "Средняя частная клиника теряет 20–40 записей в месяц просто из-за пропущенных сообщений.",
      "problem.leak.stat": "→ −180 000 ₴ / мес",

      // ---------- directions ----------
      "directions.eyebrow": "Направления",
      "directions.h2": "Один бот, сценарии под каждое направление.",
      "directions.note":
        "Все 11 направлений уже работают. Бот отвечает из базы знаний вашей клиники и записывает пациентов, но не ставит диагнозов и не интерпретирует результаты. Новое направление — по запросу.",
      "directions.badge.live": "live",
      "directions.badge.soon": "soon",
      "directions.side.title": "Пример сценария",
      "directions.default.scen.in": "Запишите меня, пожалуйста, на приём",
      "directions.default.scen.in.label": "Пациентка",
      "directions.default.scen.out": "Конечно. Подскажите направление и удобное время.",
      "directions.default.scen.out.label": "HealBot",
      // mammo
      "directions.card.mammo.label": "Маммология",
      "directions.card.mammo.h": "Запись на маммологический приём без лишних звонков",
      "directions.card.mammo.d":
        "Бот отвечает на вопросы о ценах, подготовке и врачах из базы знаний клиники и передаёт заявку администратору — без диагнозов и интерпретации результатов.",
      "directions.card.mammo.feat.1":
        "Входной квиз на основе модели Gail / NCCN и приказа МОЗ №1368 — помогает выбрать вид приёма",
      "directions.card.mammo.feat.2": "Подготовка в напоминании: обследование на 5–12 день цикла",
      "directions.card.mammo.feat.3":
        "Напоминания за 72, 24 и 2 ч до визита; повторный вызов через 2 года",
      "directions.card.mammo.feat.4":
        "Красные флаги перехватываются до AI: фиксированный ответ 103/112 и передача администратору",
      "directions.card.mammo.scen.in": "Когда лучше прийти на УЗИ молочных желёз?",
      "directions.card.mammo.scen.out":
        "Оптимально на 5–12 день цикла. Оставьте имя и телефон — администратор перезвонит и подтвердит время, а я напомню о визите.",
      // gyn
      "directions.card.gyn.label": "Гинекология",
      "directions.card.gyn.h": "Деликатная запись к гинекологу",
      "directions.card.gyn.d":
        "Отвечает на организационные вопросы и записывает на приём; лечение не назначает, толкований не даёт.",
      "directions.card.gyn.feat.1":
        "Квиз о скрининге шейки матки (ACS 2025 / руководство МОЗ 2024): с 25 лет, ВПЧ-тест раз в 5 лет",
      "directions.card.gyn.feat.2": "Подготовка к цитологии — в напоминании перед визитом",
      "directions.card.gyn.feat.3":
        "Красные флаги беременности сразу получают экстренный шаблон и передаются администратору",
      "directions.card.gyn.feat.4": "Напоминания за 72, 24 и 2 ч до визита",
      "directions.card.gyn.scen.in": "Когда лучше прийти на цитологию?",
      "directions.card.gyn.scen.out":
        "По правилам клиники — не во время менструации, за 48 ч без вагинальных средств. Напомню о подготовке за сутки. Записать вас?",
      // dent
      "directions.card.dent.label": "Стоматология",
      "directions.card.dent.h": "Запись и повторные осмотры в стоматологии",
      "directions.card.dent.d":
        "Цены, длительность процедур и запись — из базы знаний клиники. Бот не ставит диагнозов: план лечения составляет врач на осмотре.",
      "directions.card.dent.feat.1": "Квиз по форме ADA о факторах риска кариеса",
      "directions.card.dent.feat.2":
        "Интервал повторных осмотров по факторам (NICE CG19): 3, 6 или 12 месяцев",
      "directions.card.dent.feat.3": "Напоминание о плане лечения через 3 дня после визита",
      "directions.card.dent.feat.4": "Дожим тех, кто оставил телефон, но не записался",
      "directions.card.dent.scen.in": "Сколько длится профессиональная чистка?",
      "directions.card.dent.scen.out":
        "Около часа. Оставьте имя, телефон и удобное время — администратор перезвонит и подтвердит запись.",
      // eye
      "directions.card.eye.label": "Офтальмология",
      "directions.card.eye.h": "Запись на проверку зрения и диагностику",
      "directions.card.eye.d":
        "Подскажет стоимость и длительность обследований, запишет на приём и напомнит о подготовке; выводы делает только врач.",
      "directions.card.eye.feat.1":
        "Квиз по AAO PPP 2026 и ADA (осмотр при диабете) — чтобы выбрать вид приёма",
      "directions.card.eye.feat.2":
        "Предупреждение: после расширения зрачка несколько часов не садиться за руль",
      "directions.card.eye.feat.3":
        "Внезапная потеря зрения или вспышки — экстренный шаблон и передача администратору",
      "directions.card.eye.feat.4": "Напоминания за 72, 24 и 2 ч до визита",
      "directions.card.eye.scen.in": "Можно ли после осмотра сразу садиться за руль?",
      "directions.card.eye.scen.out":
        "Если врач будет расширять зрачок — нет, несколько часов зрение будет размытым. Лучше приехать с сопровождающим или на такси. Записать вас на осмотр?",
      // ped
      "directions.card.ped.label": "Педиатрия",
      "directions.card.ped.h": "Прививки и плановые осмотры детей",
      "directions.card.ped.d":
        "Отвечает родителям о прививках, осмотрах и ценах и записывает ребёнка; опасные симптомы сразу уходят в экстренный шаблон.",
      "directions.card.ped.feat.1":
        "Квиз по календарю прививок МОЗ (редакция 2026), осмотрам Bright Futures и вехам развития",
      "directions.card.ped.feat.2":
        "Красные флаги по NICE: до 3 месяцев и 38 °C, сыпь, которая не бледнеет, судороги → 103/112",
      "directions.card.ped.feat.3": "«Возьмите карту прививок» — в напоминании перед визитом",
      "directions.card.ped.feat.4": "Повторный вызов на плановый осмотр по интервалу клиники",
      "directions.card.ped.scen.in": "Что взять с собой на прививку?",
      "directions.card.ped.scen.in.label": "Мама",
      "directions.card.ped.scen.out":
        "Карту прививок и документы ребёнка. Перед прививкой ребёнка осмотрит педиатр. Напомню за сутки. Записать на удобное время?",
      // lab
      "directions.card.lab.label": "Лаборатория",
      "directions.card.lab.h": "Подготовка к анализам и запись на забор",
      "directions.card.lab.d":
        "Помогает выбрать чекап-пакет, объясняет подготовку и записывает на забор; результаты никогда не интерпретирует — их обсуждают с врачом.",
      "directions.card.lab.feat.1": "Подбор чекап-пакета по возрасту и факторам FINDRISC",
      "directions.card.lab.feat.2": "Подготовка натощак — в напоминании перед забором",
      "directions.card.lab.feat.3": "Через 3 дня: «результаты готовы — обсудите их с врачом»",
      "directions.card.lab.feat.4":
        "Есть жалобы — бот ведёт к врачу, а не к самостоятельному пакету",
      "directions.card.lab.scen.in": "Завтра сдаю биохимию. Можно выпить кофе утром?",
      "directions.card.lab.scen.out":
        "Нет, биохимию сдают натощак: 8–12 часов без еды, только вода. Кофе можно сразу после забора.",
      // cos
      "directions.card.cos.label": "Косметология",
      "directions.card.cos.h": "Консультации и запись на процедуры",
      "directions.card.cos.d":
        "Рассказывает о процедурах, ценах и подготовке из базы знаний клиники и записывает на консультацию; результатов не обещает.",
      "directions.card.cos.feat.1":
        "Фототип по Fitzpatrick и противопоказания: беременность, изотретиноин",
      "directions.card.cos.feat.2": "Меняющаяся родинка — сначала к дерматологу",
      "directions.card.cos.feat.3": "Вопрос «как вы себя чувствуете?» через 24 ч после процедуры",
      "directions.card.cos.feat.4":
        "Осложнения после инъекций — экстренный шаблон и передача администратору",
      "directions.card.cos.scen.in": "Когда лучше делать биоревитализацию перед отпуском?",
      "directions.card.cos.scen.out":
        "По правилам клиники — минимум за 2 недели до активного солнца. Сначала консультация, на которой врач проверит противопоказания. Записать вас?",
      // gen
      "directions.card.gen.label": "Общая практика",
      "directions.card.gen.h": "Подскажет, к какому врачу записаться",
      "directions.card.gen.d":
        "Направляет жалобу к профильному специалисту и собирает заявку; сначала отсеивает неотложные состояния, диагнозов не ставит.",
      "directions.card.gen.feat.1": "Маршрутизация жалобы к профильному специалисту",
      "directions.card.gen.feat.2":
        "Сначала проверяет неотложные состояния: инсульт, боль в груди, сепсис → 103/112",
      "directions.card.gen.feat.3": "Квалификация заявки: к какому врачу и в какое удобное время",
      "directions.card.gen.feat.4":
        "Напоминания за 72, 24 и 2 ч и повторный вызов по интервалу клиники",
      "directions.card.gen.scen.in": "Неделями болит поясница. К кому мне записаться?",
      "directions.card.gen.scen.out":
        "С таким вопросом у нас принимает невролог — вт, чт, сб. Оставьте имя и телефон, и администратор подберёт время.",
      // gastro
      "directions.card.gastro.label": "Гастроэнтерология",
      "directions.card.gastro.h": "Гастроэнтерология и эндоскопия",
      "directions.card.gastro.d":
        "Записывает на консультацию или эндоскопию и вовремя присылает инструкцию по подготовке — текст вашей клиники.",
      "directions.card.gastro.feat.1":
        "Квиз-маршрутизатор: длительность жалоб, колоректальный скрининг с 45 лет, семейный анамнез",
      "directions.card.gastro.feat.2":
        "Подготовка к колоноскопии и гастроскопии — в напоминаниях за 72, 24 и 12 ч",
      "directions.card.gastro.feat.3":
        "Кровь в кале, чёрный стул или рвота кровью — экстренный шаблон и передача администратору",
      "directions.card.gastro.feat.4": "Дожим тех, кто оставил телефон, но не записался",
      "directions.card.gastro.scen.in": "Меня записали на колоноскопию. Как подготовиться?",
      "directions.card.gastro.scen.in.label": "Пациент",
      "directions.card.gastro.scen.out":
        "Инструкцию клиники пришлю по частям: за 72 ч — диета, за 24 и 12 ч — приём препарата для очищения. Если что-то непонятно — подскажет администратор.",
      // uro
      "directions.card.uro.label": "Урология",
      "directions.card.uro.h": "Урология и андрология",
      "directions.card.uro.d":
        "Деликатный режим: напоминания с нейтральным текстом, без названия услуги в уведомлениях.",
      "directions.card.uro.feat.1":
        "Нейтральные напоминания — название услуги не видно в превью уведомления",
      "directions.card.uro.feat.2":
        "Квиз только для маршрутизации: к какому врачу и на какую процедуру",
      "directions.card.uro.feat.3": "Подготовка к УЗИ и спермограмме — в напоминании",
      "directions.card.uro.feat.4":
        "Напоминание о контроле ПСА — по интервалу, который назначил врач",
      "directions.card.uro.scen.in": "Как подготовиться к спермограмме?",
      "directions.card.uro.scen.in.label": "Пациент",
      "directions.card.uro.scen.out":
        "По инструкции клиники — 2–7 дней воздержания, без алкоголя и сауны. Напоминание придёт с нейтральным текстом. Записать вас?",
      // derm
      "directions.card.derm.label": "Дерматовенерология",
      "directions.card.derm.h": "Дерматология и венерология",
      "directions.card.derm.d":
        "Записывает к дерматологу и напоминает о ежегодном осмотре родинок; фото не оценивает — это делает врач на приёме.",
      "directions.card.derm.feat.1":
        "Деликатный режим для венерологии: нейтральные тексты напоминаний",
      "directions.card.derm.feat.2": "Ежегодное напоминание о картировании родинок",
      "directions.card.derm.feat.3": "Меняющаяся родинка — приоритетная запись к врачу",
      "directions.card.derm.feat.4": "Фото не оценивает: оценку даёт только дерматолог на приёме",
      "directions.card.derm.scen.in": "Родинка за месяц стала больше. Вот фото — это опасно?",
      "directions.card.derm.scen.out":
        "Оценить родинку может только врач на осмотре — по фото я этого не делаю. Поставлю вас в приоритетную запись к дерматологу. Оставьте имя и телефон.",

      // ---------- case ----------
      "case.brand": "Кейсы · частные клиники",
      "case.quote":
        "«Реальные кейсы клиник-партнёров — скоро. По запросу готовы поделиться метриками внедрений под NDA.»",
      "case.pdf.aria": "Пример отчёта с аналитикой",
      "case.pdf.title": "Отчёт за период",
      "case.pdf.sub": "HealBot analytics",
      "case.pdf.line.1": "Записей через бот",
      "case.pdf.line.2": "Ответов вне рабочих часов",
      "case.pdf.line.3": "Средний ответ",
      "case.pdf.line.4": "Удовлетворённость",
      "case.pdf.empty": "—",
      "case.cta.full": "Читать полный кейс →",

      // ---------- case study (full page) ----------
      "case.study.title": "Как маммологическая клиника вернула 40% пропущенных записей",
      "case.study.subtitle": "Пилотное внедрение HealBot в частной клинике в Киеве",
      "case.study.clinic.codename": "частная маммологическая клиника в Киеве",
      "case.study.badge.verified": "Верифицированный пилот",
      "case.study.intro.p1":
        "Администратор клиники принимал 80–120 сообщений в день в Telegram, Instagram и по телефону. До 40% запросов терялись: ответы приходили за 30+ минут, часть — только на следующий день. Пациентки тем временем записывались к конкурентам.",
      "case.study.intro.p2":
        "Команда клиники совместно с HealBot за 7 рабочих дней адаптировала сценарии под маммологический приём: триаж по жалобам, распознавание упоминаний BI-RADS, интеграция с МИС для прямой записи в свободный слот. Администратор остался на линии — но уже как эскалационный канал, а не первичный приёмник.",
      "case.study.intro.p3":
        "За 3 месяца пилота сложилась устойчивая картина: бот держит среднее время ответа ниже 2 минут 24/7, а администратор получил 60% своего времени обратно. Все цифры ниже — реальные; источник — экспорт из МИС клиники + WayForPay-логи (для онлайн-оплат) за измеренный период.",
      "case.study.metrics.heading": "Что показал пилот",
      "case.study.metric.bookings.label": "рост онлайн-записей",
      "case.study.metric.bookings.detail": "за 3 месяца по сравнению с предыдущим кварталом",
      "case.study.metric.reply.label": "среднее время ответа пациентке",
      "case.study.metric.reply.detail": "включая ночной и выходной трафик",
      "case.study.metric.frontdesk.label": "часов в неделю возвращено админу",
      "case.study.metric.frontdesk.detail": "ранее уходивших на повторы типичных вопросов",
      "case.study.metric.empty": "TBD",
      "case.study.timeline.heading": "Как это происходило",
      "case.study.quote.heading": "Из первоисточника",
      "case.study.disclaimer.medical":
        "HealBot не является медицинским устройством и не даёт медицинских советов. Все решения по диагностике и лечению принимает врач клиники.",
      "case.study.disclaimer.results":
        "Приведённые результаты наблюдались в одной частной клинике в течение пилота AI-ассистента HealBot. Они не являются гарантией результатов для любой другой клиники.",
      "case.study.cta.primary": "Забронировать демо для своей клиники",
      "case.study.back.home": "← Вернуться на главную",
      "case.study.published.label": "Опубликовано:",
      "case.study.measured.label": "Период измерения:",
      "case.study.measured.tbd": "будет указан после публикации",
      "meta.case.title": "Кейс: HealBot в маммологической клинике в Киеве — реальные метрики",
      "meta.case.description":
        "Как частная маммологическая клиника вернула 40% пропущенных записей за 3 месяца пилота HealBot. Реальные метрики, анонимизированные детали, медицинский disclaimer.",

      // ---------- calculator ----------
      "calc.eyebrow": "ROI-калькулятор",
      "calc.h2": "Сколько HealBot вернёт в кассу.",
      "calc.lede":
        "Двигайте ползунки под свою клинику — цифры пересчитываются сразу. Расчёт консервативный: берём 60% возврата пропущенных записей.",
      "calc.bullet.1": "1 час администратора = 200 ₴",
      "calc.bullet.2": "8% времени на повторных вопросах",
      "calc.bullet.3": "60% возврата пропущенных заявок",
      "calc.slider.visits.label": "Пациентов в месяц",
      "calc.slider.visits.unit": "чел.",
      "calc.slider.visits.aria": "Пациентов в месяц",
      "calc.slider.avgCheck.label": "Средний чек приёма",
      "calc.slider.avgCheck.unit": "₴",
      "calc.slider.avgCheck.aria": "Средний чек",
      "calc.slider.missed.label": "Пропущенных заявок",
      "calc.slider.missed.unit": "%",
      "calc.slider.missed.aria": "Пропущенных заявок",
      "calc.result.saved.label": "Экономия времени администратора",
      "calc.result.saved.sub": "в месяц",
      "calc.result.recovered.label": "Возвращённый доход",
      "calc.result.recovered.sub": "в месяц",
      "calc.payback.title": "Окупаемость ≈ 7–9 дней",
      "calc.payback.sub": "на тарифе Pro · считаем от минимальной выгоды",
      "calc.payback.unit": "дн.",

      // ---------- pricing ----------
      "pricing.eyebrow": "Тарифы",
      "pricing.h2": "Прозрачно. Без скрытых лимитов.",
      "pricing.aria.currency": "Валюта",
      "pricing.per_month": "/ мес",
      // Starter
      "pricing.tier.starter.title": "Starter",
      "pricing.tier.starter.desc": "Для одиночных кабинетов и небольших клиник на старте.",
      "pricing.tier.starter.feat.1": "1 направление на выбор",
      "pricing.tier.starter.feat.2": "До 600 диалогов / мес",
      "pricing.tier.starter.feat.3": "1 язык (апгрейд в Pro — мультиязычность)",
      "pricing.tier.starter.feat.4": "Базовые сценарии записи",
      "pricing.tier.starter.feat.5": "Только Telegram",
      "pricing.tier.starter.feat.6": "Email-саппорт",
      "pricing.tier.starter.muted.1": "CRM-интеграция",
      "pricing.tier.starter.muted.2": "Аналитика и отчёты",
      "pricing.tier.starter.cta": "Начать",
      // Pro
      "pricing.tier.pro.title": "Pro",
      "pricing.tier.pro.desc": "Для клиник 2–5 врачей, которые хотят масштабироваться без найма.",
      "pricing.tier.pro.feat.1": "До 3 направлений",
      "pricing.tier.pro.feat.2": "До 3 000 диалогов / мес",
      "pricing.tier.pro.feat.3": "Telegram + WhatsApp Cloud API",
      "pricing.tier.pro.feat.4": "CRM: Bitrix24, AmoCRM, MEDESK, Doctor Eleks",
      "pricing.tier.pro.feat.5": "Полная аналитика и PDF-отчёты",
      "pricing.tier.pro.feat.6": "Мультиязычность (UA/RU/EN/PL/RO)",
      "pricing.tier.pro.feat.7": "Приоритет-саппорт 24/7",
      "pricing.tier.pro.cta": "Забронировать демо",
      // Scale
      "pricing.tier.scale.title": "Scale",
      "pricing.tier.scale.desc": "Для сетей 5+ клиник и направлений с большим потоком.",
      "pricing.tier.scale.feat.1": "Безлимит направлений",
      "pricing.tier.scale.feat.2": "До 30 000 диалогов / мес, далее pay-per-use",
      "pricing.tier.scale.feat.3": "Кастомные интеграции",
      "pricing.tier.scale.feat.4": "Dedicated аккаунт-менеджер",
      "pricing.tier.scale.feat.5": "SLA 99.9% + инфра в ЕС",
      "pricing.tier.scale.feat.6": "Собственная инфраструктура по запросу",
      "pricing.tier.scale.feat.7": "White-label опционально",
      "pricing.tier.scale.cta": "Связаться",
      // PPU
      "pricing.ppu.title": "Pay-per-use, если трафик непредсказуем",
      "pricing.ppu.subtitle.a": "Платите только за активные диалоги — ",
      "pricing.ppu.subtitle.b": " за разговор. Без абонентской платы.",
      "pricing.link.more": "Узнать больше",
      // VAT note (Phase 3.4 — отображается только на en/pl/ro; в RU-копии оставлено для паритета словарей)
      "pricing.vat.note":
        "Цены без НДС. Счета выставляет ФОП Грибан Руслан Григорьевич (Украина); клиникам ЕС при необходимости оформляем партнёрский инвойс через европейское юрлицо.",
      // ---------- pricing — checkout modal (HB-PAY) ----------
      "pricing.checkout.modal.title": "Оформление подписки",
      "pricing.checkout.modal.close": "Закрыть",
      "pricing.checkout.modal.aria_label": "Оформление оплаты",
      "pricing.checkout.email.label": "Ваш email",
      "pricing.checkout.email.placeholder": "you@clinic.com",
      "pricing.checkout.email.invalid": "Введите корректный email-адрес",
      "pricing.checkout.tier.prefix": "Тариф ",
      "pricing.checkout.submit.label": "Перейти к оплате",
      "pricing.checkout.submit.loading": "Подождите…",
      "pricing.checkout.magic.sent_title": "Проверьте почту",
      "pricing.checkout.magic.sent_body":
        "Мы отправили письмо на {{email}} со ссылкой для подтверждения. Откройте её, чтобы продолжить к защищённой оплате WayForPay.",
      "pricing.checkout.magic.resend": "Отправить ещё раз",
      "pricing.checkout.magic.resend_disabled_hint": "Доступно через {{seconds}} сек",
      "pricing.checkout.error.turnstile": "Проверка безопасности не пройдена. Попробуйте ещё раз.",
      "pricing.checkout.error.busy": "Запрос уже обрабатывается. Подождите несколько секунд.",
      "pricing.checkout.error.ratelimited": "Слишком много попыток. Попробуйте позже.",
      "pricing.checkout.error.network": "Проблема с соединением. Проверьте интернет и повторите.",
      "pricing.checkout.error.generic": "Что-то пошло не так. Напишите нам в Telegram.",
      // Страницы возврата (Phase 4.7)
      "pricing.checkout.success.title": "Оплата прошла успешно",
      "pricing.checkout.success.body":
        "Спасибо! Мы получили оплату тарифа {{tier}}. Чек отправлен на вашу почту — в течение 5 минут с вами свяжется команда HealBot.",
      "pricing.checkout.failure.title": "Платёж не удалось завершить",
      "pricing.checkout.failure.body":
        "Что-то пошло не так. Если с вас всё же списали средства — напишите нам в Telegram, мы разберёмся.",

      // ---------- comparison ----------
      "cmp.eyebrow": "Сравнение",
      "cmp.h2": "Колл-центр, регистратура или HealBot?",
      "cmp.p":
        "На дистанции от 3 месяцев — бот экономит в 3–5 раз больше, чем любой из традиционных подходов.",
      "cmp.th.criterion": "Критерий",
      "cmp.th.cc": "Колл-центр",
      "cmp.th.rec": "Регистратура полного дня",
      "cmp.th.hb": "HealBot",
      "cmp.row.1.l": "Стоимость в месяц",
      "cmp.row.1.cc": "от 25 000 ₴",
      "cmp.row.1.rec": "от 18 000 ₴",
      "cmp.row.1.hb": "от 7 900 ₴",
      "cmp.row.2.l": "Среднее время ответа",
      "cmp.row.2.cc": "4–12 минут",
      "cmp.row.2.rec": "5–30 минут",
      "cmp.row.2.hb": "< 2 минут",
      "cmp.row.3.l": "Работа ночью и по выходным",
      "cmp.row.3.cc": "Только дежурный",
      "cmp.row.3.rec": "Нет",
      "cmp.row.3.hb": "24 / 7",
      "cmp.row.4.l": "Аналитика и отчёты",
      "cmp.row.4.cc": "Отсутствует",
      "cmp.row.4.rec": "Ручные сводки",
      "cmp.row.4.hb": "PDF + дашборд",
      "cmp.row.5.l": "Мультиязычность",
      "cmp.row.5.cc": "1–2 языка",
      "cmp.row.5.rec": "1 язык",
      "cmp.row.5.hb": "UA · RU · EN · PL · RO",

      // ---------- faq ----------
      "faq.eyebrow": "FAQ",
      "faq.h2": "Частые вопросы от главврачей.",
      "faq.sub": "Если вопрос сложнее — напишите в Telegram, отвечу лично в течение дня.",
      "faq.cta.ask": "Задать вопрос",
      "faq.item.1.q": "Как защищены персональные данные пациенток?",
      "faq.item.1.a":
        "Все диалоги шифруются в канале и при хранении. По умолчанию данные размещаются в инфраструктуре команды HealBot; размещение в ЕС-регионе (Франкфурт/Амстердам) доступно на тарифе Pro и выше, другие регионы обсуждаем во время onboarding. GDPR-ready: подписываем DPA, храним данные минимально необходимый срок. Персональные медицинские данные никогда не отправляются третьим сторонам, в том числе Google (Gemini API) — в модель идут только обезличенные сценарии.",
      "faq.item.2.q": "А если пациентка не пользуется Telegram?",
      "faq.item.2.a":
        "У HealBot есть параллельный канал через Viber и веб-виджет на сайте клиники. Пациентка сама выбирает, где ей удобнее. В среднем 78% ваших пациенток уже пользуются Telegram — мы проверим это на этапе демо.",
      "faq.item.3.q": "Работаете с клиниками в Польше, Румынии, Казахстане?",
      "faq.item.3.a":
        "Да. Сценарии доступны на языках UA / RU / EN / PL / RO, для Казахстана подключаем KZ-русский и казахский под заказ. Подключаем локальные МИС и мессенджеры, принятые в каждой стране. Договор и счета — через ФОП в Украине (возможна оплата в $/€/₴/₸); для клиник ЕС при необходимости оформляем партнёрский invoice через европейское юрлицо.",
      "faq.item.4.q": "Сколько занимает внедрение?",
      "faq.item.4.a":
        "На типовое направление (маммология, гинекология, стоматология) — 5–7 рабочих дней от подписания до запуска. Адаптируем под ваш регламент, подключаем МИС, тестируем на ваших кейсах. Интеграция с нестандартной CRM — до 3 недель.",
      "faq.item.5.q": "Можно ли заменить администратора ботом?",
      "faq.item.5.a":
        "Нет, это не цель HealBot. Администратор остаётся — но перестаёт захлёбываться в повторных вопросах и получает больше времени на пациенток, которые уже в клинике. По нашему опыту внедрения, администратор тратит на 60% меньше времени на входящие.",
      "faq.item.6.q": "Что будет, если бот не сможет ответить?",
      "faq.item.6.a":
        "Бот эскалирует диалог администратору с полным контекстом. Никаких «Извините, я вас не понял» — всегда есть fallback на живого человека, и пациентка это видит. Уровень эскалации на типовых направлениях — 8–12%.",

      // ---------- cta block ----------
      "cta.eyebrow": "Демо",
      "cta.h2": "Покажем живого бота за 20 минут.",
      "cta.p":
        "Не презентация слайдов — дадим вам пообщаться с живым ботом в реальном времени и разберём, как он вписывается в ваш процесс.",
      "cta.form.h3": "Забронировать демо",
      "cta.form.name.label": "Имя",
      "cta.form.name.placeholder": "Надежда",
      "cta.form.clinic.label": "Клиника",
      "cta.form.clinic.placeholder": "Название клиники",
      "cta.form.email.label": "Email",
      "cta.form.email.placeholder": "info@your-clinic.com",
      "cta.form.phone.label": "Телефон или Telegram",
      "cta.form.phone.placeholder": "+380 67 ...",
      "cta.form.submit": "Забронировать демо",
      "cta.form.sent": "Спасибо, свяжемся сегодня ✓",
      "cta.form.magiclink":
        "Мы отправили ссылку для входа в кабинет на вашу почту. Проверьте входящие.",
      "cta.form.error": "Не удалось отправить. Попробуйте ещё раз или напишите нам.",
      "cta.form.consent.pre": "Нажимая кнопку, вы соглашаетесь с ",
      "cta.form.consent.link": "политикой конфиденциальности",
      "cta.form.consent.post": ". Данные не передаются третьим сторонам.",
      "cta.tg.h3": "Или спросите в Telegram — ассистент ответит сразу.",
      "cta.tg.p":
        "С вами будет говорить тот самый AI-ассистент, которого мы ставим клиникам: тарифы, возможности, GDPR — из нашей же базы знаний. Это и есть демонстрация продукта. Когда нужен человек, он передаёт разговор основателю.",
      "cta.tg.name": "HealBot AI",
      "cta.tg.role": "ассистент · база знаний HealBot",
      "cta.tg.btn": "Написать ассистенту",
      "cta.tg.hours": "Отвечает сразу · передаёт основателю, когда нужен человек",

      // ---------- footer ----------
      "footer.brand.body":
        "AI-ассистент для частных клиник. Работает в Telegram, Viber и веб-виджете. Основан в 2024, команда из Киева.",
      "footer.badge.gdpr": "GDPR-ready",
      "footer.badge.madein": "🇺🇦 Made in Ukraine",
      "footer.col.product.h": "Продукт",
      "footer.col.product.link.1": "Как работает",
      "footer.col.product.link.2": "Направления",
      "footer.col.product.link.3": "Кейсы",
      "footer.col.product.link.4": "Калькулятор",
      "footer.col.product.link.5": "Тарифы",
      "footer.col.directions.h": "Направления",
      "footer.col.directions.link.1": "Маммология",
      "footer.col.directions.link.2": "Гинекология",
      "footer.col.directions.link.3": "Стоматология",
      "footer.col.directions.link.4": "Офтальмология",
      "footer.col.directions.link.5": "Педиатрия",
      "footer.col.company.h": "Компания",
      "footer.col.company.link.1": "О нас",
      "footer.col.company.link.2": "Блог",
      "footer.col.company.link.3": "Карьера",
      "footer.col.company.link.4": "Контакты",
      "footer.col.company.link.5": "Партнёрам",
      "footer.col.resources.h": "Ресурсы",
      "footer.col.resources.link.1": "Privacy",
      "footer.col.resources.link.2": "Terms",
      "footer.col.resources.link.3": "Cookies",
      "footer.col.resources.link.4": "GDPR",
      "footer.col.resources.link.5": "Status",
      "footer.col.resources.link.6": "Contact",
      "footer.copyright": "© 2024–2026 HealBot · ФОП Грибан Руслан Григорович · Україна",
    },

    en: {
      // English (en-GB). Glossary for medical wording (so future translators
      // pick consistent terms): мамологія -> "mammology"; мамолог -> "mammologist";
      // тріаж -> "triage"; УЗД -> "ultrasound"; мамографія -> "mammography";
      // BI-RADS -> "BI-RADS"; ущільнення -> "lump"; реєстратура -> "front desk";
      // ФОП -> "FOP"; "Made in Ukraine" stays in English on every locale's
      // badge for brand consistency.
      // ---------- meta ----------
      "meta.title": "HealBot — AI assistant for private clinics",
      "meta.description":
        "HealBot — an AI assistant for private clinics across Europe: round-the-clock patient bookings, front-desk automation, and lighter loads for your administrators.",

      // ---------- nav ----------
      "nav.aria.main": "Main",
      "nav.aria.openmenu": "Open menu",
      "nav.aria.closemenu": "Close menu",
      "nav.aria.telegram": "Message us on Telegram",
      "nav.aria.lang": "Interface language",
      "nav.link.problem": "How it works",
      "nav.link.directions": "Specialties",
      "nav.link.case": "Case studies",
      "nav.link.calculator": "Calculator",
      "nav.link.pricing": "Pricing",
      "nav.link.faq": "FAQ",
      "nav.cta.telegram": "Telegram",
      "nav.cta.demo": "Book a demo",
      "nav.drawer.telegram": "Message us on Telegram",

      // ---------- phone mockup ----------
      "phone.aria.image": "iPhone 15 mock-up showing the HealBot Telegram interface",
      "phone.status.line": "online · replying now",
      "phone.tg.date": "Today",
      "phone.tg.input.placeholder": "Message",
      "phone.hero.1": "Good afternoon! I'd like to book an appointment with a mammologist.",
      "phone.hero.2":
        "Hello 👋 Could you tell me — do you already have ultrasound or mammography results?",
      "phone.hero.3": "Yes, I had an ultrasound 3 weeks ago.",
      "phone.hero.4": "Great. Here are the three nearest slots:",
      "phone.hero.4.q1": "Tomorrow, 10:40",
      "phone.hero.4.q2": "Thu, 15:20",
      "phone.hero.4.q3": "Fri, 09:00",
      "phone.hero.5": "Tomorrow at 10:40 works.",
      "phone.hero.6": "Booked you in with Dr Nadiya. I'll send a reminder 2 hours before 💛",
      "phone.case.1": "Good evening — my breast hurts, what should I do?",
      "phone.case.2":
        "I'm sorry to hear that. Could you tell me — is the pain on one side or both?",
      "phone.case.3": "Just on the right.",
      "phone.case.4": "Understood. Let me offer you the earliest available appointments:",
      "phone.case.4.q1": "Tomorrow, 09:30",
      "phone.case.4.q2": "Tomorrow, 14:00",

      // ---------- hero ----------
      "hero.chip": "Live since 2024",
      "hero.h1.line1": "An AI assistant",
      "hero.h1.line2a": "that takes",
      "hero.h1.line2b": "front-desk drudgery",
      "hero.h1.line3": "off your team — in Telegram.",
      "hero.lede":
        "Books patients, answers FAQs, sends appointment reminders — 24/7, in their language. It doesn't replace your administrator, but it does stop the burnout.",
      "hero.cta.primary": "Book a demo",
      "hero.cta.secondary": "Talk to the live bot",
      "hero.metrics.aria": "Performance metrics",
      "hero.metric.1.tag": "placeholder",
      "hero.metric.1.tag.verified": "verified",
      "hero.metric.1.label": "uplift in online bookings",
      "hero.metric.1.label.sub": "over 3 months",
      "hero.metric.2.tag": "placeholder",
      "hero.metric.2.tag.verified": "verified",
      "hero.metric.2.unit": " min",
      "hero.metric.2.label": "average reply",
      "hero.metric.2.label.sub": "time to patient",
      "hero.metric.3.tag": "placeholder",
      "hero.metric.3.tag.verified": "verified",
      "hero.metric.3.unit": " hrs/week",
      "hero.metric.3.label": "freed up",
      "hero.metric.3.label.sub": "at the front desk",
      "hero.video.title": "How it works in 60 seconds",
      "hero.video.meta": "autoplay · muted · loop",

      // ---------- problem ----------
      "problem.eyebrow": "The problem",
      "problem.h2.pre": "Your front desk spends",
      "problem.h2.hl": "60% of its time",
      "problem.h2.post": "answering the same questions over and over.",
      "problem.lede":
        "Every other patient leaves before you've had a chance to reply. Not because your service is bad — your phone has limits, and Instagram doesn't read itself.",
      "problem.quote.1.text":
        "I tried calling from 9 a.m. — nobody picked up. In the end I booked at a different clinic.",
      "problem.quote.1.name": "Patient, 34",
      "problem.quote.1.sub": "interview, November 2024",
      "problem.quote.2.text":
        "I messaged on Instagram on a Saturday — they replied on Tuesday. By then I'd already been to a private lab.",
      "problem.quote.2.name": "Patient, 41",
      "problem.quote.2.sub": "private-clinic patient survey",
      "problem.quote.3.text":
        "I just wanted to ask if I had to fast — spent 15 minutes listening to hold music.",
      "problem.quote.3.name": "Patient, 52",
      "problem.quote.3.sub": "Google review excerpt",
      "problem.leak.h3": "And patients are quietly walking over to your competitors.",
      "problem.leak.p":
        "An average private clinic loses 20–40 bookings a month just to missed messages.",
      "problem.leak.stat": "→ −€4,500 / month",

      // ---------- directions ----------
      "directions.eyebrow": "Specialties",
      "directions.h2": "One bot, scenarios tailored to every specialty.",
      "directions.note":
        "All 11 specialties are live. The bot answers from your clinic's own knowledge base and books patients, but it never diagnoses or interprets results. New specialties on request.",
      "directions.badge.live": "live",
      "directions.badge.soon": "soon",
      "directions.side.title": "Sample scenario",
      "directions.default.scen.in": "Could you book me in for an appointment, please?",
      "directions.default.scen.in.label": "Patient",
      "directions.default.scen.out": "Of course. Which specialty and what time would suit you?",
      "directions.default.scen.out.label": "HealBot",
      // mammo
      "directions.card.mammo.label": "Mammology",
      "directions.card.mammo.h": "Mammology bookings without the phone tag",
      "directions.card.mammo.d":
        "Answers questions about prices, preparation and doctors from the clinic's knowledge base and passes the request to the administrator — no diagnoses, no interpretation of results.",
      "directions.card.mammo.feat.1":
        "Entry quiz based on the Gail model / NCCN and Ukrainian MoH order No. 1368 — helps pick the right appointment",
      "directions.card.mammo.feat.2": "Preparation in the reminder: exam on day 5–12 of the cycle",
      "directions.card.mammo.feat.3":
        "Reminders 72, 24 and 2 h before the visit; recall in 2 years",
      "directions.card.mammo.feat.4":
        "Red flags caught before the AI: fixed 103/112 reply and handoff to the administrator",
      "directions.card.mammo.scen.in": "When is the best time to come for a breast ultrasound?",
      "directions.card.mammo.scen.out":
        "Ideally on day 5–12 of your cycle. Leave your name and phone — the administrator will call back to confirm the time, and I'll remind you before the visit.",
      // gyn
      "directions.card.gyn.label": "Gynaecology",
      "directions.card.gyn.h": "Discreet gynaecology bookings",
      "directions.card.gyn.d":
        "Handles organisational questions and books appointments; never prescribes treatment or interprets anything.",
      "directions.card.gyn.feat.1":
        "Cervical screening quiz (ACS 2025 / MoH guideline 2024): from 25, HPV test every 5 years",
      "directions.card.gyn.feat.2": "Cytology preparation included in the visit reminder",
      "directions.card.gyn.feat.3":
        "Pregnancy red flags go straight to the emergency template and the administrator",
      "directions.card.gyn.feat.4": "Reminders 72, 24 and 2 h before the visit",
      "directions.card.gyn.scen.in": "When is the best time to come for a smear test?",
      "directions.card.gyn.scen.out":
        "Per the clinic's rules — not during your period, and no vaginal products for 48 h before. I'll remind you about preparation the day before. Shall I book you in?",
      // dent
      "directions.card.dent.label": "Dentistry",
      "directions.card.dent.h": "Bookings and recall in dentistry",
      "directions.card.dent.d":
        "Prices, procedure length and booking from the clinic's knowledge base. The bot doesn't diagnose: the treatment plan is made by the dentist at the visit.",
      "directions.card.dent.feat.1": "Quiz on caries risk factors based on the ADA form",
      "directions.card.dent.feat.2": "Recall interval by factors (NICE CG19): 3, 6 or 12 months",
      "directions.card.dent.feat.3": "Treatment-plan follow-up 3 days after the visit",
      "directions.card.dent.feat.4": "Nudges patients who left a phone number but didn't book",
      "directions.card.dent.scen.in": "How long does a professional cleaning take?",
      "directions.card.dent.scen.out":
        "About an hour. Leave your name, phone and a convenient time — the administrator will call back to confirm the booking.",
      // eye
      "directions.card.eye.label": "Ophthalmology",
      "directions.card.eye.h": "Eye-test and diagnostics bookings",
      "directions.card.eye.d":
        "Tells patients the price and length of exams, books the visit and sends preparation reminders; conclusions are for the doctor only.",
      "directions.card.eye.feat.1":
        "Quiz based on AAO PPP 2026 and ADA (diabetic eye exam) — to pick the right appointment",
      "directions.card.eye.feat.2": "Warning: no driving for a few hours after pupil dilation",
      "directions.card.eye.feat.3":
        "Sudden vision loss or flashes — emergency template and handoff to the administrator",
      "directions.card.eye.feat.4": "Reminders 72, 24 and 2 h before the visit",
      "directions.card.eye.scen.in": "Can I drive straight after the exam?",
      "directions.card.eye.scen.out":
        "If the doctor dilates your pupils — no, your vision will be blurred for a few hours. Better come with someone or take a taxi. Shall I book the exam?",
      // ped
      "directions.card.ped.label": "Paediatrics",
      "directions.card.ped.h": "Vaccinations and routine check-ups for children",
      "directions.card.ped.d":
        "Answers parents about vaccines, check-ups and prices and books the child in; dangerous symptoms go straight to the emergency template.",
      "directions.card.ped.feat.1":
        "Quiz based on the MoH vaccination calendar (2026 edition), Bright Futures check-ups and development milestones",
      "directions.card.ped.feat.2":
        "NICE red flags: under 3 months with 38 °C, non-blanching rash, seizures → 103/112",
      "directions.card.ped.feat.3": "“Bring the vaccination card” — in the visit reminder",
      "directions.card.ped.feat.4": "Recall for the next routine check-up at the clinic's interval",
      "directions.card.ped.scen.in": "What should we bring to the vaccination?",
      "directions.card.ped.scen.in.label": "Parent",
      "directions.card.ped.scen.out":
        "The vaccination card and the child's documents. A paediatrician will examine your child before the shot. I'll remind you the day before. Shall I book a convenient time?",
      // lab
      "directions.card.lab.label": "Laboratory",
      "directions.card.lab.h": "Test preparation and blood-draw bookings",
      "directions.card.lab.d":
        "Helps choose a check-up package, explains preparation and books the draw; never interprets results — those are discussed with a doctor.",
      "directions.card.lab.feat.1": "Check-up package selection by age and FINDRISC factors",
      "directions.card.lab.feat.2": "Fasting preparation in the reminder before the draw",
      "directions.card.lab.feat.3":
        "3 days later: “your results are ready — discuss them with a doctor”",
      "directions.card.lab.feat.4":
        "Symptoms? The bot sends the patient to a doctor, not to a self-picked package",
      "directions.card.lab.scen.in":
        "I'm doing a biochemistry panel tomorrow. Can I have coffee in the morning?",
      "directions.card.lab.scen.out":
        "No, the biochemistry panel is done fasting: 8–12 hours without food, water only. You can have coffee right after the draw.",
      // cos
      "directions.card.cos.label": "Cosmetology",
      "directions.card.cos.h": "Consultations and procedure bookings",
      "directions.card.cos.d":
        "Explains procedures, prices and preparation from the clinic's knowledge base and books a consultation; never promises results.",
      "directions.card.cos.feat.1":
        "Fitzpatrick skin type and contraindications: pregnancy, isotretinoin",
      "directions.card.cos.feat.2": "A changing mole — dermatologist first",
      "directions.card.cos.feat.3": "“How are you feeling?” message 24 h after the procedure",
      "directions.card.cos.feat.4":
        "Complications after injections — emergency template and handoff to the administrator",
      "directions.card.cos.scen.in": "When should I do biorevitalisation before my holiday?",
      "directions.card.cos.scen.out":
        "Per the clinic's rules — at least 2 weeks before strong sun. First comes a consultation, where the doctor checks contraindications. Shall I book you in?",
      // gen
      "directions.card.gen.label": "General practice",
      "directions.card.gen.h": "Helps patients find the right doctor",
      "directions.card.gen.d":
        "Routes the complaint to the right specialist and collects the request; screens for emergencies first and never diagnoses.",
      "directions.card.gen.feat.1": "Routes the complaint to the right specialist",
      "directions.card.gen.feat.2":
        "Screens emergencies first: stroke, chest pain, sepsis → 103/112",
      "directions.card.gen.feat.3": "Qualifies the request: which doctor and what time suits",
      "directions.card.gen.feat.4":
        "Reminders 72, 24 and 2 h before and recall at the clinic's interval",
      "directions.card.gen.scen.in": "My lower back has hurt for weeks. Who should I see?",
      "directions.card.gen.scen.out":
        "A neurologist sees patients with this — Tue, Thu, Sat. Leave your name and phone and the administrator will find a time.",
      // gastro
      "directions.card.gastro.label": "Gastroenterology",
      "directions.card.gastro.h": "Gastroenterology and endoscopy",
      "directions.card.gastro.d":
        "Books consultations and endoscopies and sends the preparation instructions on time — in your clinic's own words.",
      "directions.card.gastro.feat.1":
        "Routing quiz: how long symptoms have lasted, colorectal screening from 45, family history",
      "directions.card.gastro.feat.2":
        "Colonoscopy and gastroscopy prep delivered in reminders 72, 24 and 12 h before",
      "directions.card.gastro.feat.3":
        "Blood in stool, black stool or vomiting blood — emergency template and handoff to the administrator",
      "directions.card.gastro.feat.4": "Nudges patients who left a phone number but didn't book",
      "directions.card.gastro.scen.in": "I'm booked for a colonoscopy. How do I prepare?",
      "directions.card.gastro.scen.out":
        "I'll send the clinic's instructions in stages: diet at 72 h, the bowel-prep medication at 24 and 12 h. If anything is unclear, the administrator will help.",
      // uro
      "directions.card.uro.label": "Urology",
      "directions.card.uro.h": "Urology and andrology",
      "directions.card.uro.d":
        "Discreet mode: neutral reminder texts, with no service name in notification previews.",
      "directions.card.uro.feat.1":
        "Neutral reminders — the service name never shows in the notification preview",
      "directions.card.uro.feat.2": "Quiz for routing only: which doctor and which procedure",
      "directions.card.uro.feat.3": "Ultrasound and semen-analysis preparation in the reminder",
      "directions.card.uro.feat.4": "PSA check reminders at the interval set by the doctor",
      "directions.card.uro.scen.in": "How do I prepare for a semen analysis?",
      "directions.card.uro.scen.out":
        "Per the clinic's instructions — 2–7 days of abstinence, no alcohol or sauna. The reminder will arrive with a neutral text. Shall I book you in?",
      // derm
      "directions.card.derm.label": "Dermatovenereology",
      "directions.card.derm.h": "Dermatology and venereology",
      "directions.card.derm.d":
        "Books dermatology visits and reminds patients about the annual mole check; never assesses photos — that's for the doctor at the visit.",
      "directions.card.derm.feat.1": "Discreet mode for venereology: neutral reminder texts",
      "directions.card.derm.feat.2": "Annual mole-mapping recall",
      "directions.card.derm.feat.3": "A changing mole — priority booking with a doctor",
      "directions.card.derm.feat.4":
        "No photo assessment: only a dermatologist judges at the visit",
      "directions.card.derm.scen.in":
        "My mole got bigger in a month. Here's a photo — is it dangerous?",
      "directions.card.derm.scen.out":
        "Only a doctor can assess a mole in person — I don't do that from photos. I'll put you on priority booking with a dermatologist. Please leave your name and phone.",

      // ---------- case ----------
      "case.brand": "Case studies · private clinics",
      "case.quote":
        "“Real partner-clinic case studies coming soon. We're happy to share rollout metrics under NDA on request.”",
      "case.pdf.aria": "Sample analytics report",
      "case.pdf.title": "Period report",
      "case.pdf.sub": "HealBot analytics",
      "case.pdf.line.1": "Bookings via the bot",
      "case.pdf.line.2": "Out-of-hours replies",
      "case.pdf.line.3": "Average reply time",
      "case.pdf.line.4": "Satisfaction score",
      "case.pdf.empty": "—",
      "case.cta.full": "Read the full case study →",

      // ---------- case study (full page) ----------
      "case.study.title": "How a mammology clinic recovered 40% of missed bookings",
      "case.study.subtitle": "A HealBot pilot at a private clinic in Kyiv",
      "case.study.clinic.codename": "a private mammology clinic in Kyiv",
      "case.study.badge.verified": "Verified pilot",
      "case.study.intro.p1":
        "The clinic's front-desk administrator was handling 80–120 inbound messages a day across Telegram, Instagram, and phone. Up to 40% of enquiries fell through the cracks: replies took 30+ minutes, some only landed the next day. In the meantime, patients booked appointments with competing clinics.",
      "case.study.intro.p2":
        "Over 7 working days the clinic's team and HealBot adapted the scenarios for mammology: symptom-based triage, BI-RADS recognition, and PMS integration for direct slot booking. The administrator stayed on the line — but as the escalation channel, not the first responder.",
      "case.study.intro.p3":
        "After a 3-month pilot a stable pattern emerged: the bot holds an average reply time under 2 minutes around the clock, and the administrator got 60% of their time back. Every number below is real; the source is the clinic's PMS export plus WayForPay logs (for online payments) over the measured period.",
      "case.study.metrics.heading": "What the pilot showed",
      "case.study.metric.bookings.label": "uplift in online bookings",
      "case.study.metric.bookings.detail": "over 3 months versus the prior quarter baseline",
      "case.study.metric.reply.label": "average reply time to a patient",
      "case.study.metric.reply.detail": "including overnight and weekend traffic",
      "case.study.metric.frontdesk.label": "hours/week returned to the front desk",
      "case.study.metric.frontdesk.detail": "previously spent on repeat enquiries",
      "case.study.metric.empty": "TBD",
      "case.study.timeline.heading": "How the rollout went",
      "case.study.quote.heading": "From the source",
      "case.study.disclaimer.medical":
        "HealBot is not a medical device and does not provide medical advice. All diagnostic and treatment decisions are made by the clinic's medical staff.",
      "case.study.disclaimer.results":
        "These results were observed at a single private clinic during a pilot of HealBot's AI assistant. They are not a guarantee of outcomes for any other clinic.",
      "case.study.cta.primary": "Book a demo for your clinic",
      "case.study.back.home": "← Back to the home page",
      "case.study.published.label": "Published:",
      "case.study.measured.label": "Measurement period:",
      "case.study.measured.tbd": "to be confirmed at publication",
      "meta.case.title": "Case study: HealBot at a Kyiv mammology clinic — real metrics",
      "meta.case.description":
        "How a private mammology clinic recovered 40% of missed bookings over a 3-month HealBot pilot. Real metrics, anonymized details, medical disclaimer.",

      // ---------- calculator ----------
      "calc.eyebrow": "ROI calculator",
      "calc.h2": "How much HealBot brings back into your till.",
      "calc.lede":
        "Drag the sliders to match your clinic — numbers update live. The estimate is conservative: we recover only 60% of the missed bookings.",
      "calc.bullet.1": "1 hour of admin time = €5",
      "calc.bullet.2": "8% of time spent on repeat questions",
      "calc.bullet.3": "60% recovery of missed enquiries",
      "calc.slider.visits.label": "Patients per month",
      "calc.slider.visits.unit": "people",
      "calc.slider.visits.aria": "Patients per month",
      "calc.slider.avgCheck.label": "Average appointment value",
      "calc.slider.avgCheck.unit": "€",
      "calc.slider.avgCheck.aria": "Average value",
      "calc.slider.missed.label": "Missed enquiries",
      "calc.slider.missed.unit": "%",
      "calc.slider.missed.aria": "Missed enquiries",
      "calc.result.saved.label": "Front-desk hours saved",
      "calc.result.saved.sub": "per month",
      "calc.result.recovered.label": "Recovered revenue",
      "calc.result.recovered.sub": "per month",
      "calc.payback.title": "Payback ≈ 7–9 days",
      "calc.payback.sub": "on the Pro plan · counted from the lower bound",
      "calc.payback.unit": "days",

      // ---------- pricing ----------
      "pricing.eyebrow": "Pricing",
      "pricing.h2": "Transparent. No hidden caps.",
      "pricing.aria.currency": "Currency",
      "pricing.per_month": "/ month",
      // Starter
      "pricing.tier.starter.title": "Starter",
      "pricing.tier.starter.desc": "For solo practices and small clinics just starting out.",
      "pricing.tier.starter.feat.1": "1 specialty of your choice",
      "pricing.tier.starter.feat.2": "Up to 600 conversations / month",
      "pricing.tier.starter.feat.3": "1 language (upgrade to Pro for multilingual)",
      "pricing.tier.starter.feat.4": "Core booking scenarios",
      "pricing.tier.starter.feat.5": "Telegram only",
      "pricing.tier.starter.feat.6": "Email support",
      "pricing.tier.starter.muted.1": "CRM integration",
      "pricing.tier.starter.muted.2": "Analytics and reports",
      "pricing.tier.starter.cta": "Get started",
      // Pro
      "pricing.tier.pro.title": "Pro",
      "pricing.tier.pro.desc": "For clinics with 2–5 doctors that want to scale without hiring.",
      "pricing.tier.pro.feat.1": "Up to 3 specialties",
      "pricing.tier.pro.feat.2": "Up to 3,000 conversations / month",
      "pricing.tier.pro.feat.3": "Telegram + WhatsApp Cloud API",
      "pricing.tier.pro.feat.4": "CRM: Bitrix24, AmoCRM, MEDESK, Doctor Eleks",
      "pricing.tier.pro.feat.5": "Full analytics and PDF reports",
      "pricing.tier.pro.feat.6": "Multilingual (UA / RU / EN / PL / RO)",
      "pricing.tier.pro.feat.7": "Priority 24/7 support",
      "pricing.tier.pro.cta": "Book a demo",
      // Scale
      "pricing.tier.scale.title": "Scale",
      "pricing.tier.scale.desc": "For networks with 5+ clinics and high-volume specialties.",
      "pricing.tier.scale.feat.1": "Unlimited specialties",
      "pricing.tier.scale.feat.2": "Up to 30,000 conversations / month, then pay-per-use",
      "pricing.tier.scale.feat.3": "Custom integrations",
      "pricing.tier.scale.feat.4": "Dedicated account manager",
      "pricing.tier.scale.feat.5": "99.9% SLA + EU-region infrastructure",
      "pricing.tier.scale.feat.6": "Dedicated infrastructure on request",
      "pricing.tier.scale.feat.7": "White-label optional",
      "pricing.tier.scale.cta": "Contact sales",
      // PPU
      "pricing.ppu.title": "Pay-per-use, if your traffic is unpredictable",
      "pricing.ppu.subtitle.a": "Pay only for active conversations — ",
      "pricing.ppu.subtitle.b": " per chat. No monthly subscription.",
      "pricing.link.more": "Learn more",
      // VAT note (added Phase 3.4 — visible only on en/pl/ro).
      // 2026-04-26 audit rework: the previous copy promised a "European
      // entity partner invoice" that does not yet exist and omitted the
      // fact that current settlements run through WayForPay in UAH. Copy
      // now discloses the UAH-equivalent billing honestly and notes
      // native EUR/PLN/RON billing as planned, not delivered.
      "pricing.vat.note":
        "Prices are charged in their UAH equivalent via WayForPay (Ukraine) at the day's exchange rate; your bank statement may show a small FX variance. Invoices are issued by FOP Hryban Ruslan Hryhorovych. Native EUR/PLN/RON billing through a European entity is planned for a later release — talk to us if you need an EU-issued invoice today.",
      // ---------- pricing — checkout modal (HB-PAY) ----------
      "pricing.checkout.modal.title": "Subscription checkout",
      "pricing.checkout.modal.close": "Close",
      "pricing.checkout.modal.aria_label": "Payment checkout",
      "pricing.checkout.email.label": "Your email",
      "pricing.checkout.email.placeholder": "you@clinic.com",
      "pricing.checkout.email.invalid": "Please enter a valid email address",
      "pricing.checkout.tier.prefix": "Plan ",
      "pricing.checkout.submit.label": "Continue to payment",
      "pricing.checkout.submit.loading": "Please wait…",
      "pricing.checkout.magic.sent_title": "Check your inbox",
      "pricing.checkout.magic.sent_body":
        "We sent a confirmation link to {{email}}. Open it to continue to the secure WayForPay checkout. The checkout page is in English; your invoice will be in your language.",
      "pricing.checkout.magic.resend": "Resend",
      "pricing.checkout.magic.resend_disabled_hint": "Available in {{seconds}} sec",
      "pricing.checkout.error.turnstile": "Bot check failed. Please try again.",
      "pricing.checkout.error.busy": "Your request is already being processed. One moment…",
      "pricing.checkout.error.ratelimited": "Too many attempts. Please try again later.",
      "pricing.checkout.error.network": "Connection problem. Check your internet and retry.",
      "pricing.checkout.error.generic": "Something went wrong. Message us on Telegram.",
      // Success / failure return-pages (Phase 4.7)
      "pricing.checkout.success.title": "Payment successful",
      "pricing.checkout.success.body":
        "Thank you! We have received payment for the {{tier}} plan. A receipt is on its way to your inbox — the HealBot team will reach out within 5 minutes.",
      "pricing.checkout.failure.title": "Payment could not be completed",
      "pricing.checkout.failure.body":
        "Something went wrong. If you were charged, please message us on Telegram and we'll sort it out.",

      // ---------- comparison ----------
      "cmp.eyebrow": "Comparison",
      "cmp.h2": "Call centre, front desk, or HealBot?",
      "cmp.p":
        "Over a 3-month horizon the bot saves 3–5× more than any of the traditional approaches.",
      "cmp.th.criterion": "Criterion",
      "cmp.th.cc": "Call centre",
      "cmp.th.rec": "Full-time front desk",
      "cmp.th.hb": "HealBot",
      "cmp.row.1.l": "Monthly cost",
      "cmp.row.1.cc": "from €600",
      "cmp.row.1.rec": "from €450",
      "cmp.row.1.hb": "from €199",
      "cmp.row.2.l": "Average reply time",
      "cmp.row.2.cc": "4–12 minutes",
      "cmp.row.2.rec": "5–30 minutes",
      "cmp.row.2.hb": "< 2 minutes",
      "cmp.row.3.l": "Nights and weekends",
      "cmp.row.3.cc": "On-call only",
      "cmp.row.3.rec": "No",
      "cmp.row.3.hb": "24 / 7",
      "cmp.row.4.l": "Analytics and reports",
      "cmp.row.4.cc": "None",
      "cmp.row.4.rec": "Manual roll-ups",
      "cmp.row.4.hb": "PDF + dashboard",
      "cmp.row.5.l": "Multilingual support",
      "cmp.row.5.cc": "1–2 languages",
      "cmp.row.5.rec": "1 language",
      "cmp.row.5.hb": "UA · RU · EN · PL · RO",

      // ---------- faq ----------
      "faq.eyebrow": "FAQ",
      "faq.h2": "Common questions from chief physicians.",
      "faq.sub":
        "If your question is more specific — message us on Telegram, I'll reply personally within the day.",
      "faq.cta.ask": "Ask a question",
      "faq.item.1.q": "How are patients' personal data protected?",
      "faq.item.1.a":
        "Every conversation is encrypted in transit and at rest. By default, data lives in HealBot's infrastructure; EU-region hosting (Frankfurt / Amsterdam) is available on Pro and above, with other regions discussed during onboarding. GDPR-ready: we sign a DPA, retain data for the minimum necessary period, and never share personal medical data with third parties — including Google (Gemini API), where only de-identified scenarios are sent to the model.",
      "faq.item.2.q": "What if a patient doesn't use Telegram?",
      "faq.item.2.a":
        "HealBot has a parallel channel via Viber and a web widget on the clinic's site. The patient picks whichever is easier. On average 78% of your patients already use Telegram — we'll verify this during the demo.",
      "faq.item.3.q": "Do you work with clinics in Poland, Romania, or Kazakhstan?",
      "faq.item.3.a":
        "Yes. Scenarios are available in UA / RU / EN / PL / RO; Kazakh and KZ-Russian variants are added on request. We connect to the local PMS and messengers used in each market. Contracts and invoices go through a Ukrainian sole proprietorship (FOP) with payment in $/€/₴/₸; for EU clinics we can issue a partner invoice via our European entity on request.",
      "faq.item.4.q": "How long does roll-out take?",
      "faq.item.4.a":
        "For a typical specialty (mammology, gynaecology, dentistry) — 5–7 working days from sign-off to launch. We adapt to your workflow, plug in the PMS, and test on your real cases. A non-standard CRM integration takes up to 3 weeks.",
      "faq.item.5.q": "Can the bot replace our administrator?",
      "faq.item.5.a":
        "No, that isn't HealBot's goal. The administrator stays on — they just stop drowning in repeat questions and get more time for patients who are already in the clinic. From our deployments: front-desk staff spend 60% less time on inbound.",
      "faq.item.6.q": "What happens if the bot can't answer?",
      "faq.item.6.a":
        "It escalates the conversation to the administrator with full context. No \"sorry, I didn't understand\" — there's always a human fallback, and the patient sees that. Escalation rate on typical specialties: 8–12%.",

      // ---------- cta block ----------
      "cta.eyebrow": "Demo",
      "cta.h2": "We'll show you the live bot in 20 minutes.",
      "cta.p":
        "Not a slide deck — you'll talk to a real bot in real time and we'll work out how it fits your process.",
      "cta.form.h3": "Book a demo",
      "cta.form.name.label": "Name",
      "cta.form.name.placeholder": "Anna",
      "cta.form.clinic.label": "Clinic",
      "cta.form.clinic.placeholder": "Clinic name",
      "cta.form.email.label": "Email",
      "cta.form.email.placeholder": "info@your-clinic.com",
      "cta.form.phone.label": "Phone or Telegram",
      "cta.form.phone.placeholder": "+44 20 ...",
      "cta.form.submit": "Book a demo",
      "cta.form.sent": "Thank you, we'll be in touch today ✓",
      "cta.form.magiclink":
        "We've emailed you a link to sign in to your cabinet. Check your inbox.",
      "cta.form.error": "Something went wrong. Please try again or message us.",
      "cta.form.consent.pre": "By submitting you agree to our ",
      "cta.form.consent.link": "privacy policy",
      "cta.form.consent.post": ". Your data is never shared with third parties.",
      "cta.tg.h3": "Or ask on Telegram — the assistant replies right away.",
      "cta.tg.p":
        "You’ll be talking to the very assistant we deploy for clinics: pricing, features, GDPR — all from our own knowledge base. That is the product demo. When a human is needed, it hands the conversation to the founder.",
      "cta.tg.name": "HealBot AI",
      "cta.tg.role": "assistant · HealBot knowledge base",
      "cta.tg.btn": "Message the assistant",
      "cta.tg.hours": "Replies instantly · hands over to the founder when a human is needed",

      // ---------- footer ----------
      "footer.brand.body":
        "An AI assistant for private clinics. Works in Telegram, Viber, and a web widget. Founded in 2024 by a team based in Kyiv.",
      "footer.badge.gdpr": "GDPR-ready",
      "footer.badge.madein": "🇺🇦 Made in Ukraine",
      "footer.col.product.h": "Product",
      "footer.col.product.link.1": "How it works",
      "footer.col.product.link.2": "Specialties",
      "footer.col.product.link.3": "Case studies",
      "footer.col.product.link.4": "Calculator",
      "footer.col.product.link.5": "Pricing",
      "footer.col.directions.h": "Specialties",
      "footer.col.directions.link.1": "Mammology",
      "footer.col.directions.link.2": "Gynaecology",
      "footer.col.directions.link.3": "Dentistry",
      "footer.col.directions.link.4": "Ophthalmology",
      "footer.col.directions.link.5": "Paediatrics",
      "footer.col.company.h": "Company",
      "footer.col.company.link.1": "About",
      "footer.col.company.link.2": "Blog",
      "footer.col.company.link.3": "Careers",
      "footer.col.company.link.4": "Contact",
      "footer.col.company.link.5": "Partners",
      "footer.col.resources.h": "Resources",
      "footer.col.resources.link.1": "Privacy",
      "footer.col.resources.link.2": "Terms",
      "footer.col.resources.link.3": "Cookies",
      "footer.col.resources.link.4": "GDPR",
      "footer.col.resources.link.5": "Status",
      "footer.col.resources.link.6": "Contact",
      "footer.copyright": "© 2024–2026 HealBot · FOP Hryban Ruslan Hryhorovych · Ukraine",
    },

    pl: {
      // Polski (pl-PL). Glosariusz pojęć medycznych: мамологія -> "mammografia"
      // (procedura) / "senologia" (specjalność); тріаж -> "triaż"; УЗД -> "USG";
      // BI-RADS -> "BI-RADS"; ущільнення -> "guzek"; реєстратура -> "rejestracja";
      // ФОП -> "FOP" (zachowane fonetycznie); badge "Made in Ukraine" pozostaje
      // po angielsku dla spójności brandu we wszystkich locale'ach.
      // ---------- meta ----------
      "meta.title": "HealBot — asystent AI dla prywatnych klinik w Polsce",
      "meta.description":
        "HealBot — asystent AI dla prywatnych klinik: całodobowa rejestracja pacjentów, automatyzacja recepcji i mniejsze obciążenie administratorów.",

      // ---------- nav ----------
      "nav.aria.main": "Main",
      "nav.aria.openmenu": "Otwórz menu",
      "nav.aria.closemenu": "Zamknij menu",
      "nav.aria.telegram": "Napisz do nas na Telegramie",
      "nav.aria.lang": "Język interfejsu",
      "nav.link.problem": "Jak to działa",
      "nav.link.directions": "Specjalizacje",
      "nav.link.case": "Case studies",
      "nav.link.calculator": "Kalkulator",
      "nav.link.pricing": "Cennik",
      "nav.link.faq": "FAQ",
      "nav.cta.telegram": "Telegram",
      "nav.cta.demo": "Zarezerwuj demo",
      "nav.drawer.telegram": "Napisz do nas na Telegramie",

      // ---------- phone mockup ----------
      "phone.aria.image": "Makieta iPhone'a 15 z interfejsem HealBot w Telegramie",
      "phone.status.line": "online · odpowiada teraz",
      "phone.tg.date": "Dziś",
      "phone.tg.input.placeholder": "Wiadomość",
      "phone.hero.1": "Dzień dobry! Chciałabym umówić się na wizytę u mammologa.",
      "phone.hero.2": "Witam 👋 Proszę powiedzieć — czy ma już Pani wyniki USG lub mammografii?",
      "phone.hero.3": "Tak, USG miałam 3 tygodnie temu.",
      "phone.hero.4": "Świetnie. Proponuję trzy najbliższe terminy:",
      "phone.hero.4.q1": "Jutro, 10:40",
      "phone.hero.4.q2": "Czw, 15:20",
      "phone.hero.4.q3": "Pt, 09:00",
      "phone.hero.5": "Jutro o 10:40 pasuje.",
      "phone.hero.6":
        "Zarezerwowałam wizytę u dr Nadii. Wyślę przypomnienie 2 godziny wcześniej 💛",
      "phone.case.1": "Dobry wieczór, boli mnie pierś, co robić?",
      "phone.case.2":
        "Bardzo mi przykro. Proszę powiedzieć — ból jest po jednej stronie czy po obu?",
      "phone.case.3": "Tylko z prawej.",
      "phone.case.4": "Rozumiem. Proponuję najbliższe wolne terminy:",
      "phone.case.4.q1": "Jutro, 09:30",
      "phone.case.4.q2": "Jutro, 14:00",

      // ---------- hero ----------
      "hero.chip": "Pracujemy od 2024",
      "hero.h1.line1": "Asystent AI,",
      "hero.h1.line2a": "który zdejmuje",
      "hero.h1.line2b": "rutynę z recepcji",
      "hero.h1.line3": "z Państwa zespołu — w Telegramie.",
      "hero.lede":
        "Rezerwuje wizyty, odpowiada na typowe pytania, przypomina o wizycie — 24/7, w języku pacjenta. Nie zastępuje administratora, ale chroni go przed wypaleniem.",
      "hero.cta.primary": "Zarezerwuj demo",
      "hero.cta.secondary": "Porozmawiaj z botem na żywo",
      "hero.metrics.aria": "Wskaźniki efektywności",
      "hero.metric.1.tag": "placeholder",
      "hero.metric.1.tag.verified": "zweryfikowane",
      "hero.metric.1.label": "wzrost rejestracji online",
      "hero.metric.1.label.sub": "w ciągu 3 miesięcy",
      "hero.metric.2.tag": "placeholder",
      "hero.metric.2.tag.verified": "zweryfikowane",
      "hero.metric.2.unit": " min",
      "hero.metric.2.label": "średni czas",
      "hero.metric.2.label.sub": "odpowiedzi pacjentowi",
      "hero.metric.3.tag": "placeholder",
      "hero.metric.3.tag.verified": "zweryfikowane",
      "hero.metric.3.unit": " godz./tydz.",
      "hero.metric.3.label": "uwalnia",
      "hero.metric.3.label.sub": "rejestrację",
      "hero.video.title": "Jak to działa w 60 sekund",
      "hero.video.meta": "autoplay · muted · loop",

      // ---------- problem ----------
      "problem.eyebrow": "Problem",
      "problem.h2.pre": "Recepcja poświęca",
      "problem.h2.hl": "60% czasu",
      "problem.h2.post": "na powtarzanie tego samego.",
      "problem.lede":
        "Co druga pacjentka rezygnuje, zanim zdążycie odpowiedzieć. Nie dlatego, że obsługa jest zła — telefon ma swoje granice, a Instagram sam się nie czyta.",
      "problem.quote.1.text":
        "Próbowałam dodzwonić się od 9 rano — nikt nie odbierał. W końcu zarezerwowałam w innej klinice.",
      "problem.quote.1.name": "Pacjentka, 34",
      "problem.quote.1.sub": "wywiad, listopad 2024",
      "problem.quote.2.text":
        "Napisałam na Instagramie w sobotę — odpowiedzieli we wtorek. W międzyczasie pojechałam do prywatnego laboratorium.",
      "problem.quote.2.name": "Pacjentka, 41",
      "problem.quote.2.sub": "ankieta pacjentek prywatnej kliniki",
      "problem.quote.3.text":
        "Chciałam tylko zapytać, czy na czczo — 15 minut słuchałam muzyki na linii.",
      "problem.quote.3.name": "Pacjentka, 52",
      "problem.quote.3.sub": "fragment opinii z Google",
      "problem.leak.h3": "A pacjentki tymczasem przechodzą do konkurencji.",
      "problem.leak.p":
        "Średnia prywatna klinika traci 20–40 wizyt miesięcznie tylko z powodu nieodebranych wiadomości.",
      "problem.leak.stat": "→ −18 000 zł / mies.",

      // ---------- directions ----------
      "directions.eyebrow": "Specjalizacje",
      "directions.h2": "Jeden bot, scenariusze dopasowane do każdej specjalizacji.",
      "directions.note":
        "Działa już 11 specjalizacji. Bot odpowiada na podstawie bazy wiedzy Państwa kliniki i umawia pacjentów, ale nie stawia diagnoz i nie interpretuje wyników. Nowa specjalizacja — na życzenie.",
      "directions.badge.live": "live",
      "directions.badge.soon": "soon",
      "directions.side.title": "Przykład scenariusza",
      "directions.default.scen.in": "Proszę umówić mnie na wizytę.",
      "directions.default.scen.in.label": "Pacjentka",
      "directions.default.scen.out":
        "Oczywiście. Proszę powiedzieć — jaka specjalizacja i dogodny termin?",
      "directions.default.scen.out.label": "HealBot",
      // mammo
      "directions.card.mammo.label": "Mammologia",
      "directions.card.mammo.h": "Zapisy do mammologa bez zbędnych telefonów",
      "directions.card.mammo.d":
        "Bot odpowiada na pytania o ceny, przygotowanie i lekarzy z bazy wiedzy kliniki i przekazuje zgłoszenie administratorowi — bez diagnoz i interpretacji wyników.",
      "directions.card.mammo.feat.1":
        "Quiz wstępny oparty na modelu Gail / NCCN i zarządzeniu MZ Ukrainy nr 1368 — pomaga wybrać rodzaj wizyty",
      "directions.card.mammo.feat.2": "Przygotowanie w przypomnieniu: badanie w 5.–12. dniu cyklu",
      "directions.card.mammo.feat.3":
        "Przypomnienia 72, 24 i 2 h przed wizytą; ponowne zaproszenie po 2 latach",
      "directions.card.mammo.feat.4":
        "Czerwone flagi wychwytywane przed AI: stała odpowiedź 103/112 i przekazanie administratorowi",
      "directions.card.mammo.scen.in": "Kiedy najlepiej przyjść na USG piersi?",
      "directions.card.mammo.scen.out":
        "Najlepiej w 5.–12. dniu cyklu. Proszę zostawić imię i telefon — administrator oddzwoni i potwierdzi termin, a ja przypomnę o wizycie.",
      // gyn
      "directions.card.gyn.label": "Ginekologia",
      "directions.card.gyn.h": "Dyskretne zapisy do ginekologa",
      "directions.card.gyn.d":
        "Odpowiada na pytania organizacyjne i umawia wizyty; nie zleca leczenia i niczego nie interpretuje.",
      "directions.card.gyn.feat.1":
        "Quiz o profilaktyce raka szyjki macicy (ACS 2025 / wytyczne MZ Ukrainy 2024): od 25 lat, test HPV co 5 lat",
      "directions.card.gyn.feat.2": "Przygotowanie do cytologii — w przypomnieniu przed wizytą",
      "directions.card.gyn.feat.3":
        "Czerwone flagi w ciąży od razu trafiają do szablonu ratunkowego i administratora",
      "directions.card.gyn.feat.4": "Przypomnienia 72, 24 i 2 h przed wizytą",
      "directions.card.gyn.scen.in": "Kiedy najlepiej przyjść na cytologię?",
      "directions.card.gyn.scen.out":
        "Zgodnie z zasadami kliniki — nie w trakcie miesiączki i bez środków dopochwowych przez 48 h. Przypomnę o przygotowaniu dzień wcześniej. Umówić wizytę?",
      // dent
      "directions.card.dent.label": "Stomatologia",
      "directions.card.dent.h": "Zapisy i wizyty kontrolne w stomatologii",
      "directions.card.dent.d":
        "Ceny, czas trwania zabiegów i zapisy z bazy wiedzy kliniki. Bot nie stawia diagnoz: plan leczenia ustala lekarz na wizycie.",
      "directions.card.dent.feat.1": "Quiz o czynnikach ryzyka próchnicy według formularza ADA",
      "directions.card.dent.feat.2":
        "Odstęp między wizytami kontrolnymi według czynników (NICE CG19): 3, 6 lub 12 miesięcy",
      "directions.card.dent.feat.3": "Przypomnienie o planie leczenia 3 dni po wizycie",
      "directions.card.dent.feat.4":
        "Ponowny kontakt z osobami, które zostawiły telefon, ale się nie zapisały",
      "directions.card.dent.scen.in": "Ile trwa profesjonalne czyszczenie zębów?",
      "directions.card.dent.scen.out":
        "Około godziny. Proszę zostawić imię, telefon i dogodną porę — administrator oddzwoni i potwierdzi zapis.",
      // eye
      "directions.card.eye.label": "Okulistyka",
      "directions.card.eye.h": "Zapisy na badanie wzroku i diagnostykę",
      "directions.card.eye.d":
        "Podaje cenę i czas badań, umawia wizytę i przypomina o przygotowaniu; wnioski wyciąga wyłącznie lekarz.",
      "directions.card.eye.feat.1":
        "Quiz według AAO PPP 2026 i ADA (badanie przy cukrzycy) — by wybrać rodzaj wizyty",
      "directions.card.eye.feat.2":
        "Ostrzeżenie: przez kilka godzin po rozszerzeniu źrenic nie prowadzić auta",
      "directions.card.eye.feat.3":
        "Nagła utrata wzroku lub błyski — szablon ratunkowy i przekazanie administratorowi",
      "directions.card.eye.feat.4": "Przypomnienia 72, 24 i 2 h przed wizytą",
      "directions.card.eye.scen.in": "Czy po badaniu mogę od razu prowadzić samochód?",
      "directions.card.eye.scen.out":
        "Jeśli lekarz będzie rozszerzał źrenice — nie, przez kilka godzin widzenie będzie nieostre. Lepiej przyjechać z osobą towarzyszącą lub taksówką. Umówić badanie?",
      // ped
      "directions.card.ped.label": "Pediatria",
      "directions.card.ped.h": "Szczepienia i bilanse zdrowia dzieci",
      "directions.card.ped.d":
        "Odpowiada rodzicom na pytania o szczepienia, bilanse i ceny oraz zapisuje dziecko; groźne objawy od razu trafiają do szablonu ratunkowego.",
      "directions.card.ped.feat.1":
        "Quiz według kalendarza szczepień MZ Ukrainy (wyd. 2026), bilansów Bright Futures i etapów rozwoju",
      "directions.card.ped.feat.2":
        "Czerwone flagi według NICE: poniżej 3 miesięcy i 38 °C, wysypka niebledniejąca, drgawki → 103/112",
      "directions.card.ped.feat.3":
        "„Proszę zabrać kartę szczepień” — w przypomnieniu przed wizytą",
      "directions.card.ped.feat.4":
        "Zaproszenie na kolejny bilans w odstępie ustalonym przez klinikę",
      "directions.card.ped.scen.in": "Co zabrać na szczepienie?",
      "directions.card.ped.scen.in.label": "Mama",
      "directions.card.ped.scen.out":
        "Kartę szczepień i dokumenty dziecka. Przed szczepieniem dziecko zbada pediatra. Przypomnę dzień wcześniej. Umówić dogodny termin?",
      // lab
      "directions.card.lab.label": "Laboratorium",
      "directions.card.lab.h": "Przygotowanie do badań i zapisy na pobranie",
      "directions.card.lab.d":
        "Pomaga wybrać pakiet badań, wyjaśnia przygotowanie i zapisuje na pobranie; nigdy nie interpretuje wyników — omawia się je z lekarzem.",
      "directions.card.lab.feat.1": "Dobór pakietu badań według wieku i czynników FINDRISC",
      "directions.card.lab.feat.2": "Przygotowanie na czczo — w przypomnieniu przed pobraniem",
      "directions.card.lab.feat.3": "Po 3 dniach: „wyniki są gotowe — proszę omówić je z lekarzem”",
      "directions.card.lab.feat.4":
        "Są dolegliwości — bot kieruje do lekarza, a nie do samodzielnie wybranego pakietu",
      "directions.card.lab.scen.in": "Jutro robię biochemię. Mogę rano wypić kawę?",
      "directions.card.lab.scen.out":
        "Nie, biochemię robi się na czczo: 8–12 godzin bez jedzenia, tylko woda. Kawę można wypić zaraz po pobraniu.",
      // cos
      "directions.card.cos.label": "Kosmetologia",
      "directions.card.cos.h": "Konsultacje i zapisy na zabiegi",
      "directions.card.cos.d":
        "Opowiada o zabiegach, cenach i przygotowaniu z bazy wiedzy kliniki i umawia konsultację; nie obiecuje efektów.",
      "directions.card.cos.feat.1":
        "Fototyp według Fitzpatricka i przeciwwskazania: ciąża, izotretynoina",
      "directions.card.cos.feat.2": "Zmieniające się znamię — najpierw do dermatologa",
      "directions.card.cos.feat.3": "Wiadomość „jak samopoczucie?” 24 h po zabiegu",
      "directions.card.cos.feat.4":
        "Powikłania po iniekcjach — szablon ratunkowy i przekazanie administratorowi",
      "directions.card.cos.scen.in": "Kiedy zrobić biorewitalizację przed urlopem?",
      "directions.card.cos.scen.out":
        "Zgodnie z zasadami kliniki — co najmniej 2 tygodnie przed intensywnym słońcem. Najpierw konsultacja, na której lekarz sprawdzi przeciwwskazania. Umówić wizytę?",
      // gen
      "directions.card.gen.label": "Praktyka ogólna",
      "directions.card.gen.h": "Podpowie, do którego lekarza się zapisać",
      "directions.card.gen.d":
        "Kieruje zgłoszenie do właściwego specjalisty i zbiera dane do zapisu; najpierw wyłapuje stany nagłe, nie stawia diagnoz.",
      "directions.card.gen.feat.1": "Kierowanie dolegliwości do właściwego specjalisty",
      "directions.card.gen.feat.2":
        "Najpierw sprawdza stany nagłe: udar, ból w klatce piersiowej, sepsa → 103/112",
      "directions.card.gen.feat.3":
        "Kwalifikacja zgłoszenia: do którego lekarza i w jakim terminie",
      "directions.card.gen.feat.4":
        "Przypomnienia 72, 24 i 2 h przed wizytą oraz ponowne zaproszenie w odstępie ustalonym przez klinikę",
      "directions.card.gen.scen.in": "Od tygodni boli mnie krzyż. Do kogo się zapisać?",
      "directions.card.gen.scen.out":
        "Z takim problemem przyjmuje u nas neurolog — wt, czw, sob. Proszę zostawić imię i telefon, a administrator dobierze termin.",
      // gastro
      "directions.card.gastro.label": "Gastroenterologia",
      "directions.card.gastro.h": "Gastroenterologia i endoskopia",
      "directions.card.gastro.d":
        "Umawia konsultacje i endoskopie oraz na czas wysyła instrukcję przygotowania — tekstem Państwa kliniki.",
      "directions.card.gastro.feat.1":
        "Quiz kierujący: czas trwania dolegliwości, badania przesiewowe jelita grubego od 45 lat, wywiad rodzinny",
      "directions.card.gastro.feat.2":
        "Przygotowanie do kolonoskopii i gastroskopii — w przypomnieniach 72, 24 i 12 h przed",
      "directions.card.gastro.feat.3":
        "Krew w stolcu, czarny stolec lub wymioty z krwią — szablon ratunkowy i przekazanie administratorowi",
      "directions.card.gastro.feat.4":
        "Ponowny kontakt z osobami, które zostawiły telefon, ale się nie zapisały",
      "directions.card.gastro.scen.in": "Mam termin kolonoskopii. Jak się przygotować?",
      "directions.card.gastro.scen.in.label": "Pacjent",
      "directions.card.gastro.scen.out":
        "Instrukcję kliniki wyślę etapami: 72 h przed — dieta, 24 i 12 h przed — preparat do oczyszczania jelit. Jeśli coś będzie niejasne, pomoże administrator.",
      // uro
      "directions.card.uro.label": "Urologia",
      "directions.card.uro.h": "Urologia i andrologia",
      "directions.card.uro.d":
        "Tryb dyskretny: przypomnienia o neutralnej treści, bez nazwy usługi w podglądzie powiadomień.",
      "directions.card.uro.feat.1":
        "Neutralne przypomnienia — nazwa usługi nie pojawia się w podglądzie powiadomienia",
      "directions.card.uro.feat.2":
        "Quiz wyłącznie do kierowania: do którego lekarza i na jaki zabieg",
      "directions.card.uro.feat.3": "Przygotowanie do USG i badania nasienia — w przypomnieniu",
      "directions.card.uro.feat.4":
        "Przypomnienie o kontroli PSA — w odstępie ustalonym przez lekarza",
      "directions.card.uro.scen.in": "Jak przygotować się do badania nasienia?",
      "directions.card.uro.scen.in.label": "Pacjent",
      "directions.card.uro.scen.out":
        "Zgodnie z instrukcją kliniki — 2–7 dni wstrzemięźliwości, bez alkoholu i sauny. Przypomnienie przyjdzie z neutralną treścią. Umówić wizytę?",
      // derm
      "directions.card.derm.label": "Dermatowenerologia",
      "directions.card.derm.h": "Dermatologia i wenerologia",
      "directions.card.derm.d":
        "Umawia wizyty u dermatologa i przypomina o corocznej kontroli znamion; nie ocenia zdjęć — robi to lekarz na wizycie.",
      "directions.card.derm.feat.1": "Tryb dyskretny dla wenerologii: neutralne treści przypomnień",
      "directions.card.derm.feat.2": "Coroczne przypomnienie o mapowaniu znamion",
      "directions.card.derm.feat.3": "Zmieniające się znamię — priorytetowy zapis do lekarza",
      "directions.card.derm.feat.4": "Nie ocenia zdjęć: ocenę wydaje tylko dermatolog na wizycie",
      "directions.card.derm.scen.in":
        "Znamię w ciągu miesiąca się powiększyło. Oto zdjęcie — czy to groźne?",
      "directions.card.derm.scen.out":
        "Znamię może ocenić tylko lekarz podczas badania — na podstawie zdjęcia tego nie robię. Zapiszę Panią priorytetowo do dermatologa. Proszę zostawić imię i telefon.",

      // ---------- case ----------
      "case.brand": "Case studies · prywatne kliniki",
      "case.quote":
        "«Realne case studies klinik partnerskich — wkrótce. Na życzenie udostępniamy wskaźniki wdrożeń pod NDA.»",
      "case.pdf.aria": "Przykładowy raport z analityką",
      "case.pdf.title": "Raport okresowy",
      "case.pdf.sub": "HealBot analytics",
      "case.pdf.line.1": "Rezerwacje przez bota",
      "case.pdf.line.2": "Odpowiedzi poza godzinami",
      "case.pdf.line.3": "Średni czas odpowiedzi",
      "case.pdf.line.4": "Satysfakcja",
      "case.pdf.empty": "—",
      "case.cta.full": "Przeczytaj pełne case study →",

      // ---------- case study (full page) ----------
      "case.study.title": "Jak klinika senologiczna odzyskała 40% nieobsłużonych rezerwacji",
      "case.study.subtitle": "Pilotażowe wdrożenie HealBot w prywatnej klinice w Kijowie",
      "case.study.clinic.codename": "prywatna klinika senologiczna w Kijowie",
      "case.study.badge.verified": "Zweryfikowany pilotaż",
      "case.study.intro.p1":
        "Recepcjonistka kliniki obsługiwała 80–120 wiadomości dziennie w Telegramie, na Instagramie i przez telefon. Do 40% zapytań ginęło: odpowiedzi przychodziły po 30+ minutach, część dopiero następnego dnia. Tymczasem pacjentki rezerwowały wizyty u konkurencji.",
      "case.study.intro.p2":
        "W ciągu 7 dni roboczych zespół kliniki razem z HealBot dostosował scenariusze do wizyt senologicznych: triaż po objawach, rozpoznawanie wzmianek o BI-RADS, integrację z systemem klinicznym (PMS) dla bezpośredniej rezerwacji w wolnym terminie. Recepcja pozostała na linii — ale już jako kanał eskalacji, nie pierwszego kontaktu.",
      "case.study.intro.p3":
        "Po 3 miesiącach pilotażu obraz ustabilizował się: bot utrzymuje średni czas odpowiedzi poniżej 2 minut przez całą dobę, a recepcjonistka odzyskała 60% swojego czasu. Wszystkie liczby poniżej są rzeczywiste; źródło: eksport z systemu klinicznego (PMS) plus logi WayForPay (płatności online) za zmierzony okres.",
      "case.study.metrics.heading": "Co pokazał pilotaż",
      "case.study.metric.bookings.label": "wzrost rejestracji online",
      "case.study.metric.bookings.detail": "w ciągu 3 miesięcy w porównaniu z poprzednim kwartałem",
      "case.study.metric.reply.label": "średni czas odpowiedzi pacjentowi",
      "case.study.metric.reply.detail": "wliczając ruch nocny i weekendowy",
      "case.study.metric.frontdesk.label": "godzin tygodniowo zwróconych recepcji",
      "case.study.metric.frontdesk.detail": "wcześniej tracone na powtarzające się pytania",
      "case.study.metric.empty": "TBD",
      "case.study.timeline.heading": "Jak przebiegało wdrożenie",
      "case.study.quote.heading": "Z pierwszej ręki",
      "case.study.disclaimer.medical":
        "HealBot nie jest wyrobem medycznym i nie udziela porad medycznych. Wszystkie decyzje diagnostyczne i terapeutyczne podejmuje personel medyczny kliniki.",
      "case.study.disclaimer.results":
        "Przedstawione wyniki zaobserwowano w jednej prywatnej klinice podczas pilotażu asystenta AI HealBot. Nie są one gwarancją wyników dla jakiejkolwiek innej kliniki.",
      "case.study.cta.primary": "Zarezerwuj demo dla swojej kliniki",
      "case.study.back.home": "← Wróć na stronę główną",
      "case.study.published.label": "Opublikowano:",
      "case.study.measured.label": "Okres pomiaru:",
      "case.study.measured.tbd": "zostanie podany przy publikacji",
      "meta.case.title": "Case study: HealBot w klinice senologicznej w Kijowie — realne wskaźniki",
      "meta.case.description":
        "Jak prywatna klinika senologiczna odzyskała 40% nieobsłużonych rezerwacji w ciągu 3 miesięcy pilotażu HealBot. Realne wskaźniki, zanonimizowane szczegóły, disclaimer medyczny.",

      // ---------- calculator ----------
      "calc.eyebrow": "Kalkulator ROI",
      "calc.h2": "Ile HealBot zwróci do kasy.",
      "calc.lede":
        "Przesuwajcie suwaki pod swoją klinikę — liczby przeliczają się od razu. Kalkulacja jest konserwatywna: liczymy 60% odzyskanych wizyt.",
      "calc.bullet.1": "1 godzina pracy administratora = 25 zł",
      "calc.bullet.2": "8% czasu na powtarzających się pytaniach",
      "calc.bullet.3": "60% odzyskanych zgłoszeń",
      "calc.slider.visits.label": "Pacjenci miesięcznie",
      "calc.slider.visits.unit": "osób",
      "calc.slider.visits.aria": "Pacjenci miesięcznie",
      "calc.slider.avgCheck.label": "Średnia wartość wizyty",
      "calc.slider.avgCheck.unit": "zł",
      "calc.slider.avgCheck.aria": "Średnia wartość",
      "calc.slider.missed.label": "Nieodebrane zgłoszenia",
      "calc.slider.missed.unit": "%",
      "calc.slider.missed.aria": "Nieodebrane zgłoszenia",
      "calc.result.saved.label": "Oszczędność czasu administratora",
      "calc.result.saved.sub": "miesięcznie",
      "calc.result.recovered.label": "Odzyskany przychód",
      "calc.result.recovered.sub": "miesięcznie",
      "calc.payback.title": "Zwrot ≈ 7–9 dni",
      "calc.payback.sub": "na planie Pro · liczymy od minimalnej korzyści",
      "calc.payback.unit": "dni",

      // ---------- pricing ----------
      "pricing.eyebrow": "Cennik",
      "pricing.h2": "Przejrzyście. Bez ukrytych limitów.",
      "pricing.aria.currency": "Waluta",
      "pricing.per_month": "/ mies.",
      // Starter
      "pricing.tier.starter.title": "Starter",
      "pricing.tier.starter.desc": "Dla pojedynczych gabinetów i małych klinik na starcie.",
      "pricing.tier.starter.feat.1": "1 specjalizacja do wyboru",
      "pricing.tier.starter.feat.2": "Do 600 rozmów / mies.",
      "pricing.tier.starter.feat.3": "1 język (upgrade do Pro — wielojęzyczność)",
      "pricing.tier.starter.feat.4": "Podstawowe scenariusze rezerwacji",
      "pricing.tier.starter.feat.5": "Tylko Telegram",
      "pricing.tier.starter.feat.6": "Wsparcie e-mail",
      "pricing.tier.starter.muted.1": "Integracja z CRM",
      "pricing.tier.starter.muted.2": "Analityka i raporty",
      "pricing.tier.starter.cta": "Zacznij",
      // Pro
      "pricing.tier.pro.title": "Pro",
      "pricing.tier.pro.desc":
        "Dla klinik z 2–5 lekarzami, które chcą skalować się bez rekrutacji.",
      "pricing.tier.pro.feat.1": "Do 3 specjalizacji",
      "pricing.tier.pro.feat.2": "Do 3 000 rozmów / mies.",
      "pricing.tier.pro.feat.3": "Telegram + WhatsApp Cloud API",
      "pricing.tier.pro.feat.4": "CRM: Bitrix24, AmoCRM, MEDESK, Doctor Eleks",
      "pricing.tier.pro.feat.5": "Pełna analityka i raporty PDF",
      "pricing.tier.pro.feat.6": "Wielojęzyczność (UA / RU / EN / PL / RO)",
      "pricing.tier.pro.feat.7": "Wsparcie priorytetowe 24/7",
      "pricing.tier.pro.cta": "Zarezerwuj demo",
      // Scale
      "pricing.tier.scale.title": "Scale",
      "pricing.tier.scale.desc": "Dla sieci 5+ klinik i specjalizacji o dużym ruchu.",
      "pricing.tier.scale.feat.1": "Bez limitu specjalizacji",
      "pricing.tier.scale.feat.2": "Do 30 000 rozmów / mies., dalej pay-per-use",
      "pricing.tier.scale.feat.3": "Integracje na zamówienie",
      "pricing.tier.scale.feat.4": "Dedykowany account manager",
      "pricing.tier.scale.feat.5": "SLA 99,9% + infrastruktura w UE",
      "pricing.tier.scale.feat.6": "Własna infrastruktura na życzenie",
      "pricing.tier.scale.feat.7": "White-label opcjonalnie",
      "pricing.tier.scale.cta": "Skontaktuj się",
      // PPU
      "pricing.ppu.title": "Pay-per-use, gdy ruch jest nieprzewidywalny",
      "pricing.ppu.subtitle.a": "Płacicie tylko za aktywne rozmowy — ",
      "pricing.ppu.subtitle.b": " za rozmowę. Bez abonamentu.",
      "pricing.link.more": "Dowiedz się więcej",
      // VAT note (Phase 3.4 — widoczna tylko na en/pl/ro).
      // 2026-04-26 audit rework: poprzednia treść obiecywała „europejski
      // podmiot", który jeszcze nie istnieje, i pomijała fakt rozliczania
      // w UAH przez WayForPay. Tekst teraz uczciwie informuje o
      // równowartości UAH oraz traktuje natywne fakturowanie EUR/PLN/RON
      // jako planowane, a nie dostępne.
      "pricing.vat.note":
        "Płatność jest pobierana w równowartości UAH przez WayForPay (Ukraina) po kursie dnia — na wyciągu bankowym może pojawić się niewielka różnica kursowa. Faktury wystawia FOP Hryban Ruslan Hryhorovych. Natywne rozliczenia w EUR/PLN/RON przez europejski podmiot planujemy w późniejszym wydaniu — jeśli już teraz potrzebujecie faktury wystawionej w UE, napiszcie do nas.",
      // ---------- pricing — checkout modal (HB-PAY) ----------
      "pricing.checkout.modal.title": "Zamówienie subskrypcji",
      "pricing.checkout.modal.close": "Zamknij",
      "pricing.checkout.modal.aria_label": "Płatność za subskrypcję",
      "pricing.checkout.email.label": "Twój e-mail",
      "pricing.checkout.email.placeholder": "ty@klinika.pl",
      "pricing.checkout.email.invalid": "Wprowadź poprawny adres e-mail",
      "pricing.checkout.tier.prefix": "Plan ",
      "pricing.checkout.submit.label": "Przejdź do płatności",
      "pricing.checkout.submit.loading": "Proszę czekać…",
      "pricing.checkout.magic.sent_title": "Sprawdź skrzynkę",
      "pricing.checkout.magic.sent_body":
        "Wysłaliśmy link potwierdzający na {{email}}. Otwórzcie go, aby przejść do bezpiecznej płatności WayForPay. Strona płatności jest po angielsku; faktura zostanie wystawiona w Państwa języku.",
      "pricing.checkout.magic.resend": "Wyślij ponownie",
      "pricing.checkout.magic.resend_disabled_hint": "Dostępne za {{seconds}} sek.",
      "pricing.checkout.error.turnstile":
        "Weryfikacja bezpieczeństwa nie powiodła się. Spróbuj ponownie.",
      "pricing.checkout.error.busy": "Żądanie jest już przetwarzane. Chwilę…",
      "pricing.checkout.error.ratelimited": "Zbyt wiele prób. Spróbuj później.",
      "pricing.checkout.error.network":
        "Problem z połączeniem. Sprawdź internet i spróbuj ponownie.",
      "pricing.checkout.error.generic": "Coś poszło nie tak. Napisz do nas na Telegramie.",
      // Strony powrotne (Phase 4.7)
      "pricing.checkout.success.title": "Płatność zakończona pomyślnie",
      "pricing.checkout.success.body":
        "Dziękujemy! Otrzymaliśmy płatność za plan {{tier}}. Potwierdzenie wysłaliśmy na e-mail — zespół HealBot skontaktuje się z Państwem w ciągu 5 minut.",
      "pricing.checkout.failure.title": "Płatność nie powiodła się",
      "pricing.checkout.failure.body":
        "Coś poszło nie tak. Jeśli środki zostały pobrane — napiszcie do nas na Telegramie, wyjaśnimy.",

      // ---------- comparison ----------
      "cmp.eyebrow": "Porównanie",
      "cmp.h2": "Call center, recepcja czy HealBot?",
      "cmp.p":
        "Na dystansie 3 miesięcy bot oszczędza 3–5 razy więcej niż jakiekolwiek tradycyjne podejście.",
      "cmp.th.criterion": "Kryterium",
      "cmp.th.cc": "Call center",
      "cmp.th.rec": "Pełnoetatowa recepcja",
      "cmp.th.hb": "HealBot",
      "cmp.row.1.l": "Koszt miesięczny",
      "cmp.row.1.cc": "od 2 800 zł",
      "cmp.row.1.rec": "od 2 100 zł",
      "cmp.row.1.hb": "od 899 zł",
      "cmp.row.2.l": "Średni czas odpowiedzi",
      "cmp.row.2.cc": "4–12 minut",
      "cmp.row.2.rec": "5–30 minut",
      "cmp.row.2.hb": "< 2 minut",
      "cmp.row.3.l": "Praca w nocy i weekendy",
      "cmp.row.3.cc": "Tylko dyżur",
      "cmp.row.3.rec": "Nie",
      "cmp.row.3.hb": "24 / 7",
      "cmp.row.4.l": "Analityka i raporty",
      "cmp.row.4.cc": "Brak",
      "cmp.row.4.rec": "Ręczne zestawienia",
      "cmp.row.4.hb": "PDF + dashboard",
      "cmp.row.5.l": "Wielojęzyczność",
      "cmp.row.5.cc": "1–2 języki",
      "cmp.row.5.rec": "1 język",
      "cmp.row.5.hb": "UA · RU · EN · PL · RO",

      // ---------- faq ----------
      "faq.eyebrow": "FAQ",
      "faq.h2": "Częste pytania od dyrektorów medycznych.",
      "faq.sub":
        "Jeśli pytanie jest bardziej złożone — napiszcie na Telegramie, odpowiem osobiście w ciągu dnia.",
      "faq.cta.ask": "Zadaj pytanie",
      "faq.item.1.q": "Jak chronione są dane osobowe pacjentów?",
      "faq.item.1.a":
        "Każda rozmowa jest szyfrowana w transmisji i w spoczynku. Domyślnie dane przechowywane są w infrastrukturze HealBot; hosting w UE (Frankfurt / Amsterdam) dostępny jest na planie Pro i wyższych, inne regiony omawiamy podczas onboardingu. Zgodność z RODO/GDPR: podpisujemy DPA, przechowujemy dane przez minimalny niezbędny czas. Dane medyczne nigdy nie są przekazywane stronom trzecim, w tym Google (Gemini API) — do modelu trafiają wyłącznie zanonimizowane scenariusze.",
      "faq.item.2.q": "A jeśli pacjentka nie korzysta z Telegrama?",
      "faq.item.2.a":
        "HealBot ma równoległy kanał przez Viber oraz widget na stronie kliniki. Pacjentka sama wybiera, co jest dla niej wygodniejsze. Średnio 78% pacjentek już używa Telegrama — sprawdzimy to na etapie demo.",
      "faq.item.3.q": "Pracujecie z klinikami w Polsce, Rumunii, Kazachstanie?",
      "faq.item.3.a":
        "Tak. Scenariusze są dostępne w językach UA / RU / EN / PL / RO; kazachski oraz KZ-rosyjski podłączamy na zamówienie. Łączymy się z lokalnymi systemami klinicznymi (PMS) i komunikatorami przyjętymi w danym kraju. Umowa i faktury — przez ukraiński FOP (możliwa płatność w $/€/zł/₸); klinikom w UE na życzenie wystawiamy fakturę partnerską z europejskiego podmiotu.",
      "faq.item.4.q": "Ile trwa wdrożenie?",
      "faq.item.4.a":
        "Dla typowej specjalizacji (mammologia, ginekologia, stomatologia) — 5–7 dni roboczych od podpisania do startu. Dostosowujemy do Państwa regulaminu, podłączamy PMS, testujemy na Państwa rzeczywistych przypadkach. Integracja z niestandardowym CRM — do 3 tygodni.",
      "faq.item.5.q": "Czy bot zastąpi administratora?",
      "faq.item.5.a":
        "Nie, to nie jest cel HealBot. Administrator zostaje — ale przestaje tonąć w powtarzających się pytaniach i ma więcej czasu dla pacjentów, którzy są już w klinice. Z naszych wdrożeń: recepcja poświęca 60% mniej czasu na rozmowy przychodzące.",
      "faq.item.6.q": "Co, jeśli bot nie potrafi odpowiedzieć?",
      "faq.item.6.a":
        "Eskaluje rozmowę do administratora wraz z pełnym kontekstem. Żadnego «przepraszam, nie zrozumiałem» — zawsze jest fallback do żywej osoby, a pacjentka to widzi. Wskaźnik eskalacji w typowych specjalizacjach: 8–12%.",

      // ---------- cta block ----------
      "cta.eyebrow": "Demo",
      "cta.h2": "Pokażemy żywego bota w 20 minut.",
      "cta.p":
        "To nie prezentacja slajdów — porozmawiacie z prawdziwym botem na żywo i wspólnie ustalimy, jak wpisuje się w Państwa proces.",
      "cta.form.h3": "Zarezerwuj demo",
      "cta.form.name.label": "Imię",
      "cta.form.name.placeholder": "Anna",
      "cta.form.clinic.label": "Klinika",
      "cta.form.clinic.placeholder": "Nazwa kliniki",
      "cta.form.email.label": "E-mail",
      "cta.form.email.placeholder": "info@panstwa-klinika.pl",
      "cta.form.phone.label": "Telefon lub Telegram",
      "cta.form.phone.placeholder": "+48 600 ...",
      "cta.form.submit": "Zarezerwuj demo",
      "cta.form.sent": "Dziękujemy, skontaktujemy się dziś ✓",
      "cta.form.magiclink":
        "Wysłaliśmy link do logowania do panelu na Twój e-mail. Sprawdź skrzynkę.",
      "cta.form.error": "Coś poszło nie tak. Spróbuj ponownie lub napisz do nas.",
      "cta.form.consent.pre": "Klikając przycisk, zgadzacie się z ",
      "cta.form.consent.link": "polityką prywatności",
      "cta.form.consent.post": ". Dane nie są przekazywane stronom trzecim.",
      "cta.tg.h3": "Albo zapytajcie na Telegramie — asystent odpowie od razu.",
      "cta.tg.p":
        "Rozmawiacie z tym samym asystentem AI, którego wdrażamy w klinikach: cennik, możliwości, GDPR — z naszej własnej bazy wiedzy. To właśnie jest demo produktu. Gdy potrzebny jest człowiek, przekazuje rozmowę założycielowi.",
      "cta.tg.name": "HealBot AI",
      "cta.tg.role": "asystent · baza wiedzy HealBot",
      "cta.tg.btn": "Napisz do asystenta",
      "cta.tg.hours": "Odpowiada od razu · przekazuje założycielowi, gdy potrzebny jest człowiek",

      // ---------- footer ----------
      "footer.brand.body":
        "Asystent AI dla prywatnych klinik. Działa w Telegramie, Viberze i widget'cie webowym. Założony w 2024, zespół z Kijowa.",
      "footer.badge.gdpr": "GDPR-ready",
      "footer.badge.madein": "🇺🇦 Made in Ukraine",
      "footer.col.product.h": "Produkt",
      "footer.col.product.link.1": "Jak to działa",
      "footer.col.product.link.2": "Specjalizacje",
      "footer.col.product.link.3": "Case studies",
      "footer.col.product.link.4": "Kalkulator",
      "footer.col.product.link.5": "Cennik",
      "footer.col.directions.h": "Specjalizacje",
      "footer.col.directions.link.1": "Mammologia",
      "footer.col.directions.link.2": "Ginekologia",
      "footer.col.directions.link.3": "Stomatologia",
      "footer.col.directions.link.4": "Okulistyka",
      "footer.col.directions.link.5": "Pediatria",
      "footer.col.company.h": "Firma",
      "footer.col.company.link.1": "O nas",
      "footer.col.company.link.2": "Blog",
      "footer.col.company.link.3": "Kariera",
      "footer.col.company.link.4": "Kontakt",
      "footer.col.company.link.5": "Partnerom",
      "footer.col.resources.h": "Zasoby",
      "footer.col.resources.link.1": "Privacy",
      "footer.col.resources.link.2": "Terms",
      "footer.col.resources.link.3": "Cookies",
      "footer.col.resources.link.4": "GDPR",
      "footer.col.resources.link.5": "Status",
      "footer.col.resources.link.6": "Contact",
      "footer.copyright": "© 2024–2026 HealBot · FOP Hryban Ruslan Hryhorovych · Ukraina",
    },

    ro: {
      // Română (ro-RO). Glosar termeni medicali: мамологія -> "senologie"
      // (specialitate) / "mamografie" (procedură); тріаж -> "triaj";
      // УЗД -> "ecografie"; BI-RADS -> "BI-RADS"; ущільнення -> "nodul";
      // реєстратура -> "recepție"; ФОП -> "FOP" (transliterat); insigna
      // "Made in Ukraine" rămâne în engleză pentru consistență de brand.
      // Diacritice: ș (U+0219), ț (U+021B), NU ş/ţ legacy.
      // ---------- meta ----------
      "meta.title": "HealBot — asistent AI pentru clinici private din România",
      "meta.description":
        "HealBot — asistent AI pentru clinici private: programări non-stop, automatizarea recepției și mai puțină presiune pe administratori.",

      // ---------- nav ----------
      "nav.aria.main": "Main",
      "nav.aria.openmenu": "Deschide meniul",
      "nav.aria.closemenu": "Închide meniul",
      "nav.aria.telegram": "Scrie-ne pe Telegram",
      "nav.aria.lang": "Limba interfeței",
      "nav.link.problem": "Cum funcționează",
      "nav.link.directions": "Specialități",
      "nav.link.case": "Studii de caz",
      "nav.link.calculator": "Calculator",
      "nav.link.pricing": "Tarife",
      "nav.link.faq": "Întrebări frecvente",
      "nav.cta.telegram": "Telegram",
      "nav.cta.demo": "Rezervă un demo",
      "nav.drawer.telegram": "Scrie-ne pe Telegram",

      // ---------- phone mockup ----------
      "phone.aria.image": "Mockup iPhone 15 cu interfața Telegram a HealBot",
      "phone.status.line": "online · răspunde acum",
      "phone.tg.date": "Astăzi",
      "phone.tg.input.placeholder": "Mesaj",
      "phone.hero.1": "Bună ziua! Aș dori să mă programez la mamolog.",
      "phone.hero.2":
        "Bună ziua 👋 Vă rog să-mi spuneți — aveți deja rezultate de ecografie sau mamografie?",
      "phone.hero.3": "Da, am făcut ecografie acum 3 săptămâni.",
      "phone.hero.4": "Excelent. Vă propun trei intervale apropiate:",
      "phone.hero.4.q1": "Mâine, 10:40",
      "phone.hero.4.q2": "Joi, 15:20",
      "phone.hero.4.q3": "Vin, 09:00",
      "phone.hero.5": "Mâine, 10:40 e potrivit.",
      "phone.hero.6": "V-am programat la dr. Nadia. Vă trimit o reamintire cu 2 ore înainte 💛",
      "phone.case.1": "Bună seara, mă doare sânul, ce să fac?",
      "phone.case.2":
        "Îmi pare rău. Spuneți-mi, vă rog — durerea e pe o singură parte sau pe ambele?",
      "phone.case.3": "Doar dreapta.",
      "phone.case.4": "Înțeleg. Vă propun cele mai apropiate programări:",
      "phone.case.4.q1": "Mâine, 09:30",
      "phone.case.4.q2": "Mâine, 14:00",

      // ---------- hero ----------
      "hero.chip": "Activi din 2024",
      "hero.h1.line1": "Un asistent AI",
      "hero.h1.line2a": "care preia",
      "hero.h1.line2b": "rutina recepției",
      "hero.h1.line3": "pe umerii săi — în Telegram.",
      "hero.lede":
        "Programează pacienți, răspunde la întrebări frecvente, trimite reamintiri — 24/7, în limba lor. Nu înlocuiește administratorul, dar previne epuizarea.",
      "hero.cta.primary": "Rezervă un demo",
      "hero.cta.secondary": "Vorbește cu botul live",
      "hero.metrics.aria": "Indicatori de performanță",
      "hero.metric.1.tag": "placeholder",
      "hero.metric.1.tag.verified": "verificat",
      "hero.metric.1.label": "creștere a programărilor online",
      "hero.metric.1.label.sub": "în 3 luni",
      "hero.metric.2.tag": "placeholder",
      "hero.metric.2.tag.verified": "verificat",
      "hero.metric.2.unit": " min",
      "hero.metric.2.label": "timp mediu",
      "hero.metric.2.label.sub": "de răspuns către pacient",
      "hero.metric.3.tag": "placeholder",
      "hero.metric.3.tag.verified": "verificat",
      "hero.metric.3.unit": " ore/săpt.",
      "hero.metric.3.label": "eliberează",
      "hero.metric.3.label.sub": "recepția",
      "hero.video.title": "Cum funcționează în 60 de secunde",
      "hero.video.meta": "autoplay · muted · loop",

      // ---------- problem ----------
      "problem.eyebrow": "Problema",
      "problem.h2.pre": "Recepția petrece",
      "problem.h2.hl": "60% din timp",
      "problem.h2.post": "repetând aceleași răspunsuri.",
      "problem.lede":
        "O pacientă din două pleacă înainte să apucați să răspundeți. Nu pentru că serviciul e slab — telefonul are limite, iar Instagram nu se citește singur.",
      "problem.quote.1.text":
        "Am încercat să sun de la 9 dimineața — nu a răspuns nimeni. În cele din urmă m-am programat la altă clinică.",
      "problem.quote.1.name": "Pacientă, 34",
      "problem.quote.1.sub": "interviu, noiembrie 2024",
      "problem.quote.2.text":
        "Am scris pe Instagram sâmbătă — au răspuns marți. Între timp am ajuns la un laborator privat.",
      "problem.quote.2.name": "Pacientă, 41",
      "problem.quote.2.sub": "sondaj pacientelor unei clinici private",
      "problem.quote.3.text":
        "Voiam doar să întreb dacă trebuie pe nemâncate — am stat 15 minute pe muzică de așteptare.",
      "problem.quote.3.name": "Pacientă, 52",
      "problem.quote.3.sub": "fragment dintr-o recenzie Google",
      "problem.leak.h3": "Iar pacientele, între timp, pleacă la concurență.",
      "problem.leak.p":
        "O clinică privată medie pierde 20–40 de programări pe lună doar din cauza mesajelor ratate.",
      "problem.leak.stat": "→ −22 000 lei / lună",

      // ---------- directions ----------
      "directions.eyebrow": "Specialități",
      "directions.h2": "Un singur bot, scenarii adaptate fiecărei specialități.",
      "directions.note":
        "Toate cele 11 specialități funcționează deja. Botul răspunde din baza de cunoștințe a clinicii dvs. și programează pacienții, dar nu pune diagnostice și nu interpretează rezultate. Specialități noi — la cerere.",
      "directions.badge.live": "live",
      "directions.badge.soon": "soon",
      "directions.side.title": "Exemplu de scenariu",
      "directions.default.scen.in": "Vă rog să mă programați la o consultație.",
      "directions.default.scen.in.label": "Pacientă",
      "directions.default.scen.out": "Sigur. Spuneți-mi specialitatea și un interval potrivit.",
      "directions.default.scen.out.label": "HealBot",
      // mammo
      "directions.card.mammo.label": "Senologie",
      "directions.card.mammo.h": "Programări la senolog fără telefoane inutile",
      "directions.card.mammo.d":
        "Botul răspunde la întrebări despre prețuri, pregătire și medici din baza de cunoștințe a clinicii și transmite cererea administratorului — fără diagnostice și fără interpretarea rezultatelor.",
      "directions.card.mammo.feat.1":
        "Chestionar inițial bazat pe modelul Gail / NCCN și ordinul MS al Ucrainei nr. 1368 — ajută la alegerea tipului de consultație",
      "directions.card.mammo.feat.2":
        "Pregătirea în reamintire: examinarea în zilele 5–12 ale ciclului",
      "directions.card.mammo.feat.3":
        "Reamintiri cu 72, 24 și 2 ore înainte de vizită; rechemare peste 2 ani",
      "directions.card.mammo.feat.4":
        "Semnalele de alarmă sunt interceptate înainte de AI: răspuns fix 103/112 și transfer la administrator",
      "directions.card.mammo.scen.in": "Când e cel mai bine să vin la ecografia mamară?",
      "directions.card.mammo.scen.out":
        "Ideal în zilele 5–12 ale ciclului. Lăsați numele și telefonul — administratorul vă sună pentru a confirma ora, iar eu vă reamintesc de vizită.",
      // gyn
      "directions.card.gyn.label": "Ginecologie",
      "directions.card.gyn.h": "Programări discrete la ginecolog",
      "directions.card.gyn.d":
        "Răspunde la întrebări organizatorice și programează consultații; nu prescrie tratamente și nu interpretează nimic.",
      "directions.card.gyn.feat.1":
        "Chestionar despre screeningul cancerului de col uterin (ACS 2025 / ghidul MS al Ucrainei 2024): de la 25 de ani, test HPV la 5 ani",
      "directions.card.gyn.feat.2":
        "Pregătirea pentru citologie — în reamintirea dinaintea vizitei",
      "directions.card.gyn.feat.3":
        "Semnalele de alarmă în sarcină primesc imediat șablonul de urgență și ajung la administrator",
      "directions.card.gyn.feat.4": "Reamintiri cu 72, 24 și 2 ore înainte de vizită",
      "directions.card.gyn.scen.in": "Când e cel mai bine să vin pentru citologie?",
      "directions.card.gyn.scen.out":
        "Conform regulilor clinicii — nu în timpul menstruației și fără produse vaginale cu 48 de ore înainte. Vă reamintesc pregătirea cu o zi înainte. Vă programez?",
      // dent
      "directions.card.dent.label": "Stomatologie",
      "directions.card.dent.h": "Programări și controale periodice în stomatologie",
      "directions.card.dent.d":
        "Prețuri, durata procedurilor și programare din baza de cunoștințe a clinicii. Botul nu pune diagnostice: planul de tratament îl face medicul la consultație.",
      "directions.card.dent.feat.1":
        "Chestionar despre factorii de risc pentru carii, după formularul ADA",
      "directions.card.dent.feat.2":
        "Intervalul controalelor după factori (NICE CG19): 3, 6 sau 12 luni",
      "directions.card.dent.feat.3": "Reamintire despre planul de tratament la 3 zile după vizită",
      "directions.card.dent.feat.4":
        "Recontactează pacienții care și-au lăsat telefonul, dar nu s-au programat",
      "directions.card.dent.scen.in": "Cât durează o igienizare profesională?",
      "directions.card.dent.scen.out":
        "Cam o oră. Lăsați numele, telefonul și o oră convenabilă — administratorul vă sună și confirmă programarea.",
      // eye
      "directions.card.eye.label": "Oftalmologie",
      "directions.card.eye.h": "Programări pentru verificarea vederii și diagnostic",
      "directions.card.eye.d":
        "Spune prețul și durata investigațiilor, programează vizita și reamintește pregătirea; concluziile le trage doar medicul.",
      "directions.card.eye.feat.1":
        "Chestionar după AAO PPP 2026 și ADA (control la diabet) — pentru alegerea tipului de consultație",
      "directions.card.eye.feat.2": "Avertisment: câteva ore după dilatarea pupilei nu conduceți",
      "directions.card.eye.feat.3":
        "Pierdere bruscă a vederii sau fulgere luminoase — șablon de urgență și transfer la administrator",
      "directions.card.eye.feat.4": "Reamintiri cu 72, 24 și 2 ore înainte de vizită",
      "directions.card.eye.scen.in": "Pot conduce imediat după control?",
      "directions.card.eye.scen.out":
        "Dacă medicul vă dilată pupila — nu, câteva ore vederea va fi încețoșată. Mai bine veniți însoțit sau cu taxiul. Vă programez la control?",
      // ped
      "directions.card.ped.label": "Pediatrie",
      "directions.card.ped.h": "Vaccinări și controale periodice pentru copii",
      "directions.card.ped.d":
        "Răspunde părinților despre vaccinuri, controale și prețuri și programează copilul; simptomele periculoase merg direct în șablonul de urgență.",
      "directions.card.ped.feat.1":
        "Chestionar după calendarul de vaccinare al MS al Ucrainei (ediția 2026), controalele Bright Futures și etapele dezvoltării",
      "directions.card.ped.feat.2":
        "Semnale de alarmă NICE: sub 3 luni cu 38 °C, erupție care nu pălește la presiune, convulsii → 103/112",
      "directions.card.ped.feat.3":
        "„Luați carnetul de vaccinări” — în reamintirea dinaintea vizitei",
      "directions.card.ped.feat.4": "Rechemare la următorul control, la intervalul clinicii",
      "directions.card.ped.scen.in": "Ce să luăm la vaccinare?",
      "directions.card.ped.scen.in.label": "Mamă",
      "directions.card.ped.scen.out":
        "Carnetul de vaccinări și actele copilului. Înainte de vaccin, copilul e consultat de pediatru. Vă reamintesc cu o zi înainte. Vă programez la o oră convenabilă?",
      // lab
      "directions.card.lab.label": "Laborator",
      "directions.card.lab.h": "Pregătire pentru analize și programare la recoltare",
      "directions.card.lab.d":
        "Ajută la alegerea unui pachet de check-up, explică pregătirea și programează recoltarea; nu interpretează niciodată rezultatele — acestea se discută cu medicul.",
      "directions.card.lab.feat.1":
        "Alegerea pachetului de check-up după vârstă și factorii FINDRISC",
      "directions.card.lab.feat.2": "Pregătirea pe nemâncate — în reamintirea dinaintea recoltării",
      "directions.card.lab.feat.3":
        "După 3 zile: „rezultatele sunt gata — discutați-le cu medicul”",
      "directions.card.lab.feat.4":
        "Aveți acuze? Botul vă trimite la medic, nu la un pachet ales singur",
      "directions.card.lab.scen.in": "Mâine fac biochimia. Pot bea cafea dimineața?",
      "directions.card.lab.scen.out":
        "Nu, biochimia se face pe nemâncate: 8–12 ore fără mâncare, doar apă. Cafeaua — imediat după recoltare.",
      // cos
      "directions.card.cos.label": "Cosmetologie",
      "directions.card.cos.h": "Consultații și programări la proceduri",
      "directions.card.cos.d":
        "Explică procedurile, prețurile și pregătirea din baza de cunoștințe a clinicii și programează consultația; nu promite rezultate.",
      "directions.card.cos.feat.1":
        "Fototipul Fitzpatrick și contraindicații: sarcină, izotretinoin",
      "directions.card.cos.feat.2": "O aluniță care se schimbă — mai întâi la dermatolog",
      "directions.card.cos.feat.3": "Mesaj „cum vă simțiți?” la 24 de ore după procedură",
      "directions.card.cos.feat.4":
        "Complicații după injecții — șablon de urgență și transfer la administrator",
      "directions.card.cos.scen.in": "Când să fac biorevitalizarea înainte de concediu?",
      "directions.card.cos.scen.out":
        "Conform regulilor clinicii — cu cel puțin 2 săptămâni înainte de soare puternic. Mai întâi o consultație, la care medicul verifică contraindicațiile. Vă programez?",
      // gen
      "directions.card.gen.label": "Medicină generală",
      "directions.card.gen.h": "Vă spune la ce medic să vă programați",
      "directions.card.gen.d":
        "Direcționează acuza către specialistul potrivit și colectează cererea; mai întâi filtrează urgențele, nu pune diagnostice.",
      "directions.card.gen.feat.1": "Direcționarea acuzei către specialistul potrivit",
      "directions.card.gen.feat.2":
        "Verifică întâi urgențele: AVC, durere în piept, sepsis → 103/112",
      "directions.card.gen.feat.3": "Calificarea cererii: la ce medic și la ce oră convenabilă",
      "directions.card.gen.feat.4":
        "Reamintiri cu 72, 24 și 2 ore înainte și rechemare la intervalul clinicii",
      "directions.card.gen.scen.in": "Mă doare spatele de săptămâni. La cine să mă programez?",
      "directions.card.gen.scen.out":
        "Cu o astfel de problemă la noi consultă neurologul — marți, joi, sâmbătă. Lăsați numele și telefonul, iar administratorul găsește o oră.",
      // gastro
      "directions.card.gastro.label": "Gastroenterologie",
      "directions.card.gastro.h": "Gastroenterologie și endoscopie",
      "directions.card.gastro.d":
        "Programează consultații și endoscopii și trimite la timp instrucțiunile de pregătire — textul clinicii dvs.",
      "directions.card.gastro.feat.1":
        "Chestionar de direcționare: durata acuzelor, screening colorectal de la 45 de ani, istoric familial",
      "directions.card.gastro.feat.2":
        "Pregătirea pentru colonoscopie și gastroscopie — în reamintirile cu 72, 24 și 12 ore înainte",
      "directions.card.gastro.feat.3":
        "Sânge în scaun, scaun negru sau vărsături cu sânge — șablon de urgență și transfer la administrator",
      "directions.card.gastro.feat.4":
        "Recontactează pacienții care și-au lăsat telefonul, dar nu s-au programat",
      "directions.card.gastro.scen.in": "Sunt programat la colonoscopie. Cum mă pregătesc?",
      "directions.card.gastro.scen.in.label": "Pacient",
      "directions.card.gastro.scen.out":
        "Vă trimit instrucțiunile clinicii pe etape: cu 72 de ore înainte — dieta, cu 24 și 12 ore — preparatul pentru curățarea intestinului. Dacă ceva nu e clar, vă ajută administratorul.",
      // uro
      "directions.card.uro.label": "Urologie",
      "directions.card.uro.h": "Urologie și andrologie",
      "directions.card.uro.d":
        "Mod discret: reamintiri cu text neutru, fără numele serviciului în previzualizarea notificărilor.",
      "directions.card.uro.feat.1":
        "Reamintiri neutre — numele serviciului nu apare în previzualizarea notificării",
      "directions.card.uro.feat.2":
        "Chestionar doar pentru direcționare: la ce medic și pentru ce procedură",
      "directions.card.uro.feat.3": "Pregătirea pentru ecografie și spermogramă — în reamintire",
      "directions.card.uro.feat.4":
        "Reamintire pentru controlul PSA — la intervalul stabilit de medic",
      "directions.card.uro.scen.in": "Cum mă pregătesc pentru spermogramă?",
      "directions.card.uro.scen.in.label": "Pacient",
      "directions.card.uro.scen.out":
        "Conform instrucțiunilor clinicii — 2–7 zile de abstinență, fără alcool și saună. Reamintirea vine cu text neutru. Vă programez?",
      // derm
      "directions.card.derm.label": "Dermatovenerologie",
      "directions.card.derm.h": "Dermatologie și venerologie",
      "directions.card.derm.d":
        "Programează la dermatolog și reamintește controlul anual al alunițelor; nu evaluează fotografii — asta o face medicul la consultație.",
      "directions.card.derm.feat.1": "Mod discret pentru venerologie: texte neutre în reamintiri",
      "directions.card.derm.feat.2": "Rechemare anuală pentru cartografierea alunițelor",
      "directions.card.derm.feat.3": "O aluniță care se schimbă — programare prioritară la medic",
      "directions.card.derm.feat.4":
        "Nu evaluează fotografii: evaluarea o face doar dermatologul la consultație",
      "directions.card.derm.scen.in": "Alunița s-a mărit într-o lună. Iată o poză — e periculos?",
      "directions.card.derm.scen.out":
        "Doar medicul poate evalua alunița la consultație — după poză nu fac asta. Vă programez prioritar la dermatolog. Lăsați numele și telefonul.",

      // ---------- case ----------
      "case.brand": "Studii de caz · clinici private",
      "case.quote":
        "„Studii de caz reale ale clinicilor partenere — în curând. La cerere putem împărtăși metricile implementărilor sub NDA.”",
      "case.pdf.aria": "Exemplu de raport cu analiză",
      "case.pdf.title": "Raport pe perioadă",
      "case.pdf.sub": "HealBot analytics",
      "case.pdf.line.1": "Programări prin bot",
      "case.pdf.line.2": "Răspunsuri în afara orelor",
      "case.pdf.line.3": "Timp mediu de răspuns",
      "case.pdf.line.4": "Satisfacție",
      "case.pdf.empty": "—",
      "case.cta.full": "Citește studiul de caz complet →",

      // ---------- case study (full page) ----------
      "case.study.title": "Cum a recuperat o clinică de senologie 40% din programările pierdute",
      "case.study.subtitle": "Pilot HealBot într-o clinică privată din Kiev",
      "case.study.clinic.codename": "o clinică privată de senologie din Kiev",
      "case.study.badge.verified": "Pilot verificat",
      "case.study.intro.p1":
        "Recepția clinicii primea 80–120 de mesaje pe zi pe Telegram, Instagram și telefon. Până la 40% dintre cereri se pierdeau: răspunsurile veneau în 30+ minute, unele abia a doua zi. Între timp, pacientele își făceau programări la clinici concurente.",
      "case.study.intro.p2":
        "În 7 zile lucrătoare echipa clinicii și HealBot au adaptat scenariile pentru consultațiile de senologie: triaj după simptome, recunoașterea mențiunilor BI-RADS, integrarea cu sistemul clinic (PMS) pentru programare directă în slot. Recepția a rămas pe linie — dar ca un canal de escaladare, nu prima linie.",
      "case.study.intro.p3":
        "După 3 luni de pilot s-a conturat un tipar stabil: botul menține un timp mediu de răspuns sub 2 minute non-stop, iar recepția a recuperat 60% din timp. Toate cifrele de mai jos sunt reale; sursa: exportul din PMS-ul clinicii plus logurile WayForPay (pentru plăți online) pe perioada măsurată.",
      "case.study.metrics.heading": "Ce a arătat pilotul",
      "case.study.metric.bookings.label": "creștere a programărilor online",
      "case.study.metric.bookings.detail": "în 3 luni față de trimestrul precedent",
      "case.study.metric.reply.label": "timp mediu de răspuns către pacient",
      "case.study.metric.reply.detail": "incluzând traficul de noapte și de weekend",
      "case.study.metric.frontdesk.label": "ore/săptămână returnate recepției",
      "case.study.metric.frontdesk.detail": "consumate anterior pe întrebări repetitive",
      "case.study.metric.empty": "TBD",
      "case.study.timeline.heading": "Cum s-a desfășurat implementarea",
      "case.study.quote.heading": "Direct de la sursă",
      "case.study.disclaimer.medical":
        "HealBot nu este un dispozitiv medical și nu oferă sfaturi medicale. Toate deciziile de diagnostic și tratament sunt luate de personalul medical al clinicii.",
      "case.study.disclaimer.results":
        "Aceste rezultate au fost observate într-o singură clinică privată în timpul unui pilot al asistentului AI HealBot. Ele nu reprezintă o garanție a rezultatelor pentru orice altă clinică.",
      "case.study.cta.primary": "Rezervă un demo pentru clinica ta",
      "case.study.back.home": "← Înapoi la pagina principală",
      "case.study.published.label": "Publicat:",
      "case.study.measured.label": "Perioada de măsurare:",
      "case.study.measured.tbd": "va fi specificată la publicare",
      "meta.case.title":
        "Studiu de caz: HealBot la o clinică de senologie din Kiev — metrici reale",
      "meta.case.description":
        "Cum a recuperat o clinică privată de senologie 40% din programările pierdute în 3 luni de pilot HealBot. Metrici reale, detalii anonimizate, disclaimer medical.",

      // ---------- calculator ----------
      "calc.eyebrow": "Calculator ROI",
      "calc.h2": "Cât aduce HealBot înapoi în casierie.",
      "calc.lede":
        "Mișcați cursoarele după clinica voastră — cifrele se recalculează imediat. Calculul e conservator: luăm 60% din programările recuperate.",
      "calc.bullet.1": "1 oră de administrator = 30 lei",
      "calc.bullet.2": "8% din timp pe întrebări repetitive",
      "calc.bullet.3": "60% recuperare a solicitărilor ratate",
      "calc.slider.visits.label": "Pacienți pe lună",
      "calc.slider.visits.unit": "persoane",
      "calc.slider.visits.aria": "Pacienți pe lună",
      "calc.slider.avgCheck.label": "Valoare medie consultație",
      "calc.slider.avgCheck.unit": "lei",
      "calc.slider.avgCheck.aria": "Valoare medie",
      "calc.slider.missed.label": "Solicitări ratate",
      "calc.slider.missed.unit": "%",
      "calc.slider.missed.aria": "Solicitări ratate",
      "calc.result.saved.label": "Timp economisit la recepție",
      "calc.result.saved.sub": "pe lună",
      "calc.result.recovered.label": "Venit recuperat",
      "calc.result.recovered.sub": "pe lună",
      "calc.payback.title": "Rambursare ≈ 7–9 zile",
      "calc.payback.sub": "pe planul Pro · calculat de la beneficiul minim",
      "calc.payback.unit": "zile",

      // ---------- pricing ----------
      "pricing.eyebrow": "Tarife",
      "pricing.h2": "Transparent. Fără limite ascunse.",
      "pricing.aria.currency": "Monedă",
      "pricing.per_month": "/ lună",
      // Starter
      "pricing.tier.starter.title": "Starter",
      "pricing.tier.starter.desc": "Pentru cabinete individuale și clinici mici la început.",
      "pricing.tier.starter.feat.1": "1 specialitate la alegere",
      "pricing.tier.starter.feat.2": "Până la 600 conversații / lună",
      "pricing.tier.starter.feat.3": "1 limbă (upgrade la Pro — multilingv)",
      "pricing.tier.starter.feat.4": "Scenarii de bază pentru programări",
      "pricing.tier.starter.feat.5": "Doar Telegram",
      "pricing.tier.starter.feat.6": "Suport prin e-mail",
      "pricing.tier.starter.muted.1": "Integrare CRM",
      "pricing.tier.starter.muted.2": "Analiză și rapoarte",
      "pricing.tier.starter.cta": "Începe",
      // Pro
      "pricing.tier.pro.title": "Pro",
      "pricing.tier.pro.desc": "Pentru clinici cu 2–5 medici care vor să crească fără angajări.",
      "pricing.tier.pro.feat.1": "Până la 3 specialități",
      "pricing.tier.pro.feat.2": "Până la 3 000 conversații / lună",
      "pricing.tier.pro.feat.3": "Telegram + WhatsApp Cloud API",
      "pricing.tier.pro.feat.4": "CRM: Bitrix24, AmoCRM, MEDESK, Doctor Eleks",
      "pricing.tier.pro.feat.5": "Analiză completă și rapoarte PDF",
      "pricing.tier.pro.feat.6": "Multilingv (UA / RU / EN / PL / RO)",
      "pricing.tier.pro.feat.7": "Suport prioritar 24/7",
      "pricing.tier.pro.cta": "Rezervă un demo",
      // Scale
      "pricing.tier.scale.title": "Scale",
      "pricing.tier.scale.desc": "Pentru rețele cu 5+ clinici și specialități cu volum mare.",
      "pricing.tier.scale.feat.1": "Specialități nelimitate",
      "pricing.tier.scale.feat.2": "Până la 30 000 conversații / lună, apoi pay-per-use",
      "pricing.tier.scale.feat.3": "Integrări personalizate",
      "pricing.tier.scale.feat.4": "Account manager dedicat",
      "pricing.tier.scale.feat.5": "SLA 99,9% + infrastructură UE",
      "pricing.tier.scale.feat.6": "Infrastructură proprie la cerere",
      "pricing.tier.scale.feat.7": "White-label opțional",
      "pricing.tier.scale.cta": "Contactează-ne",
      // PPU
      "pricing.ppu.title": "Pay-per-use, dacă traficul e imprevizibil",
      "pricing.ppu.subtitle.a": "Plătiți doar pentru conversații active — ",
      "pricing.ppu.subtitle.b": " pe conversație. Fără abonament.",
      "pricing.link.more": "Aflați mai multe",
      // Notă VAT (Phase 3.4 — vizibilă doar pe en/pl/ro).
      // 2026-04-26 audit rework: textul anterior promitea o „entitate
      // europeană" care încă nu există și nu menționa că plata se face
      // în echivalent UAH prin WayForPay. Textul informează acum onest
      // despre echivalentul UAH și prezintă facturarea nativă
      // EUR/PLN/RON ca fiind planificată, nu disponibilă.
      "pricing.vat.note":
        "Plata se procesează în echivalent UAH prin WayForPay (Ucraina) la cursul zilei; pe extrasul bancar poate apărea o mică diferență de curs. Facturile sunt emise de FOP Hryban Ruslan Hryhorovych. Facturarea nativă în EUR/PLN/RON printr-o entitate europeană este planificată pentru un release ulterior — dacă aveți nevoie acum de o factură emisă în UE, contactați-ne.",
      // ---------- pricing — checkout modal (HB-PAY) ----------
      "pricing.checkout.modal.title": "Comandă abonament",
      "pricing.checkout.modal.close": "Închide",
      "pricing.checkout.modal.aria_label": "Plata abonamentului",
      "pricing.checkout.email.label": "E-mailul dvs.",
      "pricing.checkout.email.placeholder": "tu@clinica.ro",
      "pricing.checkout.email.invalid": "Introduceți o adresă de e-mail validă",
      "pricing.checkout.tier.prefix": "Plan ",
      "pricing.checkout.submit.label": "Continuă spre plată",
      "pricing.checkout.submit.loading": "Vă rugăm așteptați…",
      "pricing.checkout.magic.sent_title": "Verificați-vă e-mailul",
      "pricing.checkout.magic.sent_body":
        "Am trimis un link de confirmare la {{email}}. Deschideți-l pentru a continua spre plata securizată WayForPay. Pagina de plată este în engleză; factura va fi în limba dvs.",
      "pricing.checkout.magic.resend": "Trimite din nou",
      "pricing.checkout.magic.resend_disabled_hint": "Disponibil în {{seconds}} sec.",
      "pricing.checkout.error.turnstile": "Verificarea de securitate a eșuat. Încercați din nou.",
      "pricing.checkout.error.busy": "Cererea este deja în procesare. Un moment…",
      "pricing.checkout.error.ratelimited": "Prea multe încercări. Reîncercați mai târziu.",
      "pricing.checkout.error.network":
        "Problemă de conexiune. Verificați internetul și reîncercați.",
      "pricing.checkout.error.generic": "Ceva a mers greșit. Scrieți-ne pe Telegram.",
      // Pagini de retur (Phase 4.7)
      "pricing.checkout.success.title": "Plată reușită",
      "pricing.checkout.success.body":
        "Mulțumim! Am primit plata pentru planul {{tier}}. Chitanța e pe drum spre e-mailul dvs. — echipa HealBot vă va contacta în 5 minute.",
      "pricing.checkout.failure.title": "Plata nu a putut fi finalizată",
      "pricing.checkout.failure.body":
        "Ceva a mers greșit. Dacă suma a fost reținută, scrieți-ne pe Telegram și clarificăm.",

      // ---------- comparison ----------
      "cmp.eyebrow": "Comparație",
      "cmp.h2": "Call center, recepție sau HealBot?",
      "cmp.p":
        "Pe orizont de 3 luni, botul economisește de 3–5 ori mai mult decât oricare dintre abordările tradiționale.",
      "cmp.th.criterion": "Criteriu",
      "cmp.th.cc": "Call center",
      "cmp.th.rec": "Recepție full-time",
      "cmp.th.hb": "HealBot",
      "cmp.row.1.l": "Cost lunar",
      "cmp.row.1.cc": "de la 3 000 lei",
      "cmp.row.1.rec": "de la 2 200 lei",
      "cmp.row.1.hb": "de la 999 lei",
      "cmp.row.2.l": "Timp mediu de răspuns",
      "cmp.row.2.cc": "4–12 minute",
      "cmp.row.2.rec": "5–30 minute",
      "cmp.row.2.hb": "< 2 minute",
      "cmp.row.3.l": "Noaptea și în weekend",
      "cmp.row.3.cc": "Doar de gardă",
      "cmp.row.3.rec": "Nu",
      "cmp.row.3.hb": "24 / 7",
      "cmp.row.4.l": "Analiză și rapoarte",
      "cmp.row.4.cc": "Lipsă",
      "cmp.row.4.rec": "Centralizări manuale",
      "cmp.row.4.hb": "PDF + dashboard",
      "cmp.row.5.l": "Suport multilingv",
      "cmp.row.5.cc": "1–2 limbi",
      "cmp.row.5.rec": "1 limbă",
      "cmp.row.5.hb": "UA · RU · EN · PL · RO",

      // ---------- faq ----------
      "faq.eyebrow": "FAQ",
      "faq.h2": "Întrebări frecvente de la directorii medicali.",
      "faq.sub":
        "Dacă întrebarea e mai complexă — scrieți pe Telegram, răspund personal în cursul zilei.",
      "faq.cta.ask": "Pune o întrebare",
      "faq.item.1.q": "Cum sunt protejate datele personale ale pacienților?",
      "faq.item.1.a":
        "Toate conversațiile sunt criptate în tranzit și în repaus. Implicit, datele sunt găzduite în infrastructura HealBot; găzduirea în UE (Frankfurt / Amsterdam) e disponibilă pe planul Pro și mai sus, alte regiuni le discutăm la onboarding. GDPR-ready: semnăm DPA, păstrăm datele pe perioada minimă necesară. Datele medicale personale nu sunt niciodată trimise unor terțe părți, inclusiv Google (Gemini API) — în model intră doar scenarii anonimizate.",
      "faq.item.2.q": "Și dacă pacienta nu folosește Telegram?",
      "faq.item.2.a":
        "HealBot are un canal paralel prin Viber și un widget pe site-ul clinicii. Pacienta alege singură ce e mai comod. În medie 78% dintre pacientele dvs. folosesc deja Telegram — vom verifica acest lucru la demo.",
      "faq.item.3.q": "Lucrați cu clinici din Polonia, România, Kazahstan?",
      "faq.item.3.a":
        "Da. Scenariile sunt disponibile în UA / RU / EN / PL / RO; pentru Kazahstan adăugăm KZ-rusa și kazaha la cerere. Conectăm sisteme clinice locale (PMS) și mesageriile folosite în fiecare țară. Contractul și facturile — prin FOP în Ucraina (plată posibilă în $/€/lei/₸); pentru clinicile UE, la nevoie, emitem o factură partener prin entitatea noastră europeană.",
      "faq.item.4.q": "Cât durează implementarea?",
      "faq.item.4.a":
        "Pentru o specialitate tipică (senologie, ginecologie, stomatologie) — 5–7 zile lucrătoare de la semnătură la lansare. Adaptăm la regulamentul dvs., conectăm PMS-ul, testăm pe cazurile reale. Integrarea cu un CRM nestandard — până la 3 săptămâni.",
      "faq.item.5.q": "Putem înlocui administratorul cu un bot?",
      "faq.item.5.a":
        "Nu, nu acesta e scopul HealBot. Administratorul rămâne — dar nu mai e copleșit de întrebări repetitive și are mai mult timp pentru pacientele deja în clinică. Din experiența noastră de implementare, recepția petrece cu 60% mai puțin timp pe inbound.",
      "faq.item.6.q": "Ce se întâmplă dacă botul nu poate răspunde?",
      "faq.item.6.a":
        "Escaladează discuția către administrator cu tot contextul. Niciun „îmi pare rău, nu am înțeles” — există întotdeauna un fallback uman, iar pacienta vede asta. Rata de escaladare pe specialități tipice: 8–12%.",

      // ---------- cta block ----------
      "cta.eyebrow": "Demo",
      "cta.h2": "Vă arătăm botul live în 20 de minute.",
      "cta.p":
        "Nu o prezentare cu slide-uri — veți vorbi cu un bot real în timp real și vom analiza cum se integrează în procesul dvs.",
      "cta.form.h3": "Rezervă un demo",
      "cta.form.name.label": "Nume",
      "cta.form.name.placeholder": "Ana",
      "cta.form.clinic.label": "Clinică",
      "cta.form.clinic.placeholder": "Numele clinicii",
      "cta.form.email.label": "E-mail",
      "cta.form.email.placeholder": "info@clinica-dvs.ro",
      "cta.form.phone.label": "Telefon sau Telegram",
      "cta.form.phone.placeholder": "+40 7xx ...",
      "cta.form.submit": "Rezervă un demo",
      "cta.form.sent": "Mulțumim, vă contactăm astăzi ✓",
      "cta.form.magiclink":
        "Ți-am trimis pe e-mail un link de autentificare în cabinet. Verifică inboxul.",
      "cta.form.error": "Ceva nu a funcționat. Încearcă din nou sau scrie-ne.",
      "cta.form.consent.pre": "Apăsând butonul, sunteți de acord cu ",
      "cta.form.consent.link": "politica de confidențialitate",
      "cta.form.consent.post": ". Datele nu sunt transmise unor terțe părți.",
      "cta.tg.h3": "Sau întrebați pe Telegram — asistentul răspunde imediat.",
      "cta.tg.p":
        "Veți vorbi cu exact același asistent AI pe care îl implementăm în clinici: tarife, funcții, GDPR — din propria noastră bază de cunoștințe. Aceasta este demonstrația produsului. Când e nevoie de un om, predă conversația fondatorului.",
      "cta.tg.name": "HealBot AI",
      "cta.tg.role": "asistent · baza de cunoștințe HealBot",
      "cta.tg.btn": "Scrie asistentului",
      "cta.tg.hours": "Răspunde imediat · predă fondatorului când e nevoie de un om",

      // ---------- footer ----------
      "footer.brand.body":
        "Asistent AI pentru clinici private. Funcționează în Telegram, Viber și un widget web. Fondat în 2024, echipă din Kiev.",
      "footer.badge.gdpr": "GDPR-ready",
      "footer.badge.madein": "🇺🇦 Made in Ukraine",
      "footer.col.product.h": "Produs",
      "footer.col.product.link.1": "Cum funcționează",
      "footer.col.product.link.2": "Specialități",
      "footer.col.product.link.3": "Studii de caz",
      "footer.col.product.link.4": "Calculator",
      "footer.col.product.link.5": "Tarife",
      "footer.col.directions.h": "Specialități",
      "footer.col.directions.link.1": "Senologie",
      "footer.col.directions.link.2": "Ginecologie",
      "footer.col.directions.link.3": "Stomatologie",
      "footer.col.directions.link.4": "Oftalmologie",
      "footer.col.directions.link.5": "Pediatrie",
      "footer.col.company.h": "Companie",
      "footer.col.company.link.1": "Despre noi",
      "footer.col.company.link.2": "Blog",
      "footer.col.company.link.3": "Carieră",
      "footer.col.company.link.4": "Contact",
      "footer.col.company.link.5": "Parteneri",
      "footer.col.resources.h": "Resurse",
      "footer.col.resources.link.1": "Privacy",
      "footer.col.resources.link.2": "Terms",
      "footer.col.resources.link.3": "Cookies",
      "footer.col.resources.link.4": "GDPR",
      "footer.col.resources.link.5": "Status",
      "footer.col.resources.link.6": "Contact",
      "footer.copyright": "© 2024–2026 HealBot · FOP Hryban Ruslan Hryhorovych · Ucraina",
    },
  };

  // ---- cookie helpers ----
  // Cookie name is inlined as the literal "healbot_locale=" for both read and write paths
  // so external grep checks (CI verify gates) can confirm the cookie lifecycle.
  const readCookie = () => {
    const parts = (document.cookie || "").split(";");
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i].trim();
      if (p.indexOf("healbot_locale=") === 0) {
        return decodeURIComponent(p.substring("healbot_locale=".length));
      }
    }
    return "";
  };
  const writeCookie = (value) => {
    document.cookie =
      "healbot_locale=" +
      encodeURIComponent(value) +
      "; Path=/; Max-Age=" +
      COOKIE_MAX_AGE +
      "; SameSite=Lax";
  };

  const isSupported = (v) => typeof v === "string" && SUPPORTED.indexOf(v) !== -1;

  // BCP-47 tag for Intl/toLocaleString. Switch covers all SUPPORTED locales
  // and falls back to uk-UA (DEFAULT) for any unexpected input.
  const numberLocale = (loc) => {
    switch (loc) {
      case "ru":
        return "ru-RU";
      case "en":
        return "en-GB";
      case "pl":
        return "pl-PL";
      case "ro":
        return "ro-RO";
      case "uk":
        return "uk-UA";
      default:
        return "uk-UA";
    }
  };

  // ---- detect ----
  // Precedence: URL prefix -> localStorage -> cookie -> navigator -> DEFAULT.
  // URL prefix wins because shared/canonical links must control the locale
  // regardless of returning users' stored preference.
  const detect = () => {
    // 1. URL prefix (/ru/, /en/, /pl/, /ro/). uk is implicit (no prefix).
    try {
      const path = (location && location.pathname) || "";
      const m = path.match(LOCALE_PATH_RE);
      if (m && isSupported(m[1])) {
        dbg("detect: URL prefix ->", m[1]);
        return m[1];
      }
    } catch (_e) {
      // location unavailable (non-browser env); continue.
    }
    // 2. localStorage (dot-notation: localStorage.healbot_locale).
    try {
      const ls = localStorage.healbot_locale;
      if (isSupported(ls)) {
        dbg("detect: localStorage ->", ls);
        return ls;
      }
    } catch (_e) {
      // localStorage unavailable (private mode); continue.
    }
    // 3. Cookie.
    const ck = readCookie();
    if (isSupported(ck)) {
      dbg("detect: cookie ->", ck);
      return ck;
    }
    // 4. navigator.languages (Accept-Language). Walk in order of preference
    //    and return the first prefix match against a SUPPORTED locale.
    const langs =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language || DEFAULT];
    for (let i = 0; i < langs.length; i++) {
      const entry = String(langs[i] || "").toLowerCase();
      // Iterate SUPPORTED so adding a locale to the array auto-extends detection.
      for (let j = 0; j < SUPPORTED.length; j++) {
        const code = SUPPORTED[j];
        if (entry.indexOf(code) === 0) {
          dbg("detect: navigator -> " + code + ' (from "' + entry + '")');
          return code;
        }
      }
    }
    // 5. Default.
    dbg("detect: default ->", DEFAULT);
    return DEFAULT;
  };

  // ---- current locale state ----
  let currentLocale = detect();

  const applyMeta = (locale) => {
    try {
      document.documentElement.lang = locale;
      const title = (strings[locale] || {})["meta.title"];
      if (title) document.title = title;
      const meta = document.querySelector('meta[name="description"]');
      const desc = (strings[locale] || {})["meta.description"];
      if (meta && desc) meta.setAttribute("content", desc);
    } catch (_e) {
      // DOM may not be ready for the very first synchronous call; safe to ignore.
    }
  };

  // Apply first-paint side-effects immediately (no event dispatch — nothing listening yet).
  applyMeta(currentLocale);

  // Sticky-detect: write our detected value to storage so the next visit is instant.
  try {
    localStorage.healbot_locale = currentLocale;
  } catch (_e) {
    // ignore
  }
  writeCookie(currentLocale);

  const getLocale = () => currentLocale;

  // ---- syncLocalePath ----
  // Rewrites the URL pathname to reflect the active locale without a full reload.
  //   uk         -> "/" + rest         (no prefix)
  //   ru/en/pl/ro -> "/" + locale + rest
  // Idempotent: bails out if the URL already matches the requested locale.
  // Uses history.replaceState to avoid polluting the back/forward stack.
  const syncLocalePath = (locale) => {
    try {
      if (!history || !location) return;
      const path = location.pathname || "/";
      const m = path.match(LOCALE_PATH_RE);
      const currentPrefix = m ? m[1] : ""; // "" means uk (implicit).
      const wantedPrefix = locale === DEFAULT ? "" : locale;
      if (currentPrefix === wantedPrefix) {
        dbg("syncLocalePath: no-op (path already matches", locale + ")");
        return;
      }
      // Strip any existing locale prefix to get the canonical rest-of-path.
      const rest = m ? path.slice(m[0].length - (m[2] || "").length) : path;
      // Ensure leading slash on the rest, then prepend the new prefix (or nothing for uk).
      const restNorm = rest.startsWith("/") ? rest : "/" + rest;
      const next = wantedPrefix ? "/" + wantedPrefix + restNorm : restNorm || "/";
      const search = location.search || "";
      const hash = location.hash || "";
      history.replaceState(null, "", next + search + hash);
      dbg("syncLocalePath:", path, "->", next);
    } catch (_e) {
      // history/location API unavailable; not fatal — locale state is already set.
    }
  };

  const setLocale = (locale) => {
    if (!isSupported(locale)) {
      throw new Error("[i18n] Unsupported locale: " + locale);
    }
    if (locale === currentLocale) {
      dbg("setLocale: no-op (already", locale + ")");
      return;
    }
    currentLocale = locale;
    try {
      localStorage.healbot_locale = locale;
    } catch (_e) {
      // ignore
    }
    writeCookie(locale);
    syncLocalePath(locale);
    applyMeta(locale);
    dbg("setLocale:", locale);
    try {
      window.dispatchEvent(new CustomEvent("i18n:change", { detail: { locale } }));
    } catch (_e) {
      // CustomEvent unsupported (very old browsers); fall back to a plain Event.
      const evt = document.createEvent("Event");
      evt.initEvent("i18n:change", true, true);
      window.dispatchEvent(evt);
    }
  };

  // Per-locale fallback chain:
  //   1. active locale dictionary
  //   2. each entry of FALLBACK[active] in order (typically uk, then en)
  //   3. DEFAULT dictionary (uk) — guaranteed last resort before user fallback
  //   4. caller-supplied fallback string (if provided)
  //   5. literal key (with a debug warning when __I18N_DEBUG__ is on)
  // Step 3 is redundant for most chains but kept explicit so adding a locale
  // without updating FALLBACK still falls through to the default dictionary.
  const t = (key, fallback) => {
    const dict = strings[currentLocale] || {};
    if (Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
    const chain = FALLBACK[currentLocale] || [];
    for (let i = 0; i < chain.length; i++) {
      const next = strings[chain[i]] || {};
      if (Object.prototype.hasOwnProperty.call(next, key)) return next[key];
    }
    const def = strings[DEFAULT] || {};
    if (Object.prototype.hasOwnProperty.call(def, key)) return def[key];
    if (typeof fallback !== "undefined") return fallback;
    if (window.__I18N_DEBUG__) console.warn("[i18n] missing key:", key);
    return key;
  };

  window.i18n = {
    SUPPORTED: SUPPORTED,
    DEFAULT: DEFAULT,
    strings: strings,
    t: t,
    getLocale: getLocale,
    setLocale: setLocale,
    numberLocale: numberLocale,
  };

  // Sanity check: make the existence of every dictionary visible in the source
  // (greppable by CI verify gates: strings.uk / strings.ru / strings.en /
  // strings.pl / strings.ro) and warn early if any is missing entirely.
  if (!strings.uk || !strings.ru || !strings.en || !strings.pl || !strings.ro) {
    console.warn(
      "[i18n] missing dictionary: strings.uk / strings.ru / strings.en / strings.pl / strings.ro"
    );
  }

  // Per-locale key-parity check (Phase 2.7). Warns when a translator has
  // missed keys from the UK source. Gated by __I18N_DEBUG__ so it never
  // spams production consoles. Runs once at boot.
  if (window.__I18N_DEBUG__) {
    const ukKeys = Object.keys(strings.uk || {});
    ["ru", "en", "pl", "ro"].forEach((loc) => {
      const dict = strings[loc] || {};
      const missing = ukKeys.filter((k) => !Object.prototype.hasOwnProperty.call(dict, k));
      if (missing.length) {
        console.warn(
          "[i18n] WARN: locale " + loc + " missing " + missing.length + " keys vs DEFAULT (uk)",
          missing.slice(0, 5)
        );
      }
    });
  }

  // Verbose readiness log: includes effective locale, fallback chain, and the
  // BCP-47 number tag so debug sessions can confirm the full Phase 1 surface
  // without needing to re-walk the constants. Gated by __I18N_DEBUG__.
  dbg(
    "ready, locale =",
    currentLocale,
    "| fallback =",
    (FALLBACK[currentLocale] || []).join(",") || "(none)",
    "| numberLocale =",
    numberLocale(currentLocale),
    "| SUPPORTED =",
    SUPPORTED.join(",")
  );
})();
